import React, { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {useUser} from "../../UserContext";



// Quiz data for different courses
const courseQuestions = {
  "101": {
    title: "CT Brain Plain Study Quiz",
    description: "Test your knowledge about CT brain plain study fundamentals",
    questions: [
      { 
        id: 1, 
        question: "Q1. What is the primary use of CT brain plain study?", 
        options: [
          "To detect intracranial haemorrhage and fractures",
          "To diagnose brain tumors",
          "To detect sinus infections",
          "To monitor migraine headaches"
        ],
        answer: "To detect intracranial haemorrhage and fractures"
        
      },
      { 
        id: 2, 
        question: "Q2. Which of the following conditions is best detected using a CT brain plain study? ", 
        options: [
          "Early ischemic stroke",
          "Acute haemorrhage",
          "Multiple sclerosis",
          "Small brain tumours"
          
        ],
        answer: "Acute haemorrhage"
      },
      { 
        id: 3, 
        question: "Q3. Why is contrast not used in a CT brain plain study for suspected stroke?", 
        options: [
          "Contrast enhances blood vessels, making fresh blood difficult to see",
          "Contrast is toxic to the brain tissue",
          "Non-contrast scans provide better soft tissue differentiation",
          "Contrast takes too long to administer"
        ],
        answer: "Contrast enhances blood vessels, making fresh blood difficult to see"
      },
      { 
        id: 4, 
        question: "Q4. Non-contrast scans provide better soft tissue differentiation", 
        options: [
          "Hypodense (dark)",
          "Isodense (same as brain tissue)",
          "Hyperdense (bright white)",
          "Mixed density with a calcified center"
        ],
        answer: "Hypodense (bright white)"
      },
      { 
        id: 5, 
        question: "Q5. What is a limitation of a CT brain plain study compared to MRI?", 
        options: [
          "It is more expensive",
          "It cannot detect skull fractures",
          "It exposes the patient to ionizing radiation",
          "It takes longer to perform"
        ],
        answer: "It exposes the patient to ionizing radiation"
      }
    ]
  },
  "102": {
    title: "Contrast-Enhanced CT Brain Quiz",
    description: "Evaluate your understanding of contrast-enhanced CT brain scans",
    questions: [
      { 
        id: 1, 
        question: "Q1. What is the primary indication for performing a contrast-enhanced CT brain scan?", 
        options: [
          "Headache without neurological deficits",
          "Acute ischemic stroke within 3 hours",
          "Evaluation of brain tumours and infections",
          "Routine migraine evaluation time"

        ],  
        answer: "Evaluation of brain tumours and infections"
      },
      { 
        id: 2, 
        question: "Q2. What is the most likely diagnosis for this CT brain finding?", 
        options: [
          "Glioblastoma Multiforme",
          "Tuberculoma",
          "Meningioma",
          "Brain Abscess"
        ],
        answer: "Brain Abscess" 
      },
      { 
        id: 3, 
        question: "Q3. Which condition is most commonly associated with this finding?", 
        options: [
          "Subarachnoid Haemorrhage",
          "Hypertensive Intracerebral Haemorrhage",
          "Cavernous Malformation",
          "Ischemic Stroke"
        ],
        answer: "Hypertensive Intracerebral Haemorrhage"
      },
      { 
        id: 4, 
        question: "Q4. Which of the following conditions typically shows enhancement on a contrast-enhanced CT brain scan?", 
        options: [
          "Meningitis",
          "Multiple Sclerosis",
          "Diffuse Axonal Injury",
          "Alzheimer's Disease"
        ]
      },
      { 
        id: 5, 
        question: "Q5. Which of the following is a contraindication for administering IV contrast in a CT brain scan?", 
        options: [
          "Suspected Brain Tumour",
          "History of Severe Contrast Allergy",
          "Evaluation of Brain Metastases",
          "Suspected Meningitis"
        ],
        answer: "History of Severe Contrast Allergy"
      }
    ]
  },
  "103": {
    title: "Clinical Case-Based CT Brain Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. A non-contrast CT brain scan shows a hypodense area in the left middle cerebral artery (MCA) territory with loss of gray-white matter differentiation. What is the most likely diagnosis?", 
        options: [
          "Ischemic Stroke",
          "Haemorrhagic Stroke",
          "Brain Tumor",
          "Normal Brain"
        ],
        answer: "Ischemic Stroke"
      },
      { 
        id: 2, 
        question: "Q2. A CT scan shows a hyperdense (bright) MCA sign on the right side without a clear infarct area yet. What does this indicate?", 
        options: [
          "MCA Thrombosis",
          "Normal Variation",
          "Subarachnoid Haemorrhage",
          "Meningioma"
        ],
        answer: "MCA Thrombosis"
      },
      { 
        id: 3, 
        question: "Q3. A 65-year-old hypertensive patient presents with sudden loss of consciousness. A CT scan shows a hyperdense area in the left basal ganglia with surrounding edema. What is the most likely diagnosis?", 
        options: [
          "Ischemic Stroke",
          "Haemorrhagic Stroke",
          "Epidural Hematoma",
          "Brain Abscess"
        ],
        answer: "Haemorrhagic Stroke"
      },
      { 
        id: 4, 
        question: "Q4. A 72-year-old male presents with sudden dizziness, vomiting, and difficulty walking. CT scan reveals a hypodense area in the cerebellum with brainstem compression. What is the next best step?", 
        options: [
          "Observe and repeat CT in 24 hours",
          "Perform an MRI",
          "Neurosurgical evaluation for decompression",
          "Start Aspirin and Clopidogrel"
        ],
        answer: "Neurosurgical evaluation for decompression"

      },
      { 
        id: 5, 
        question: "Q5. A 78-year-old patient with a history of falls presents with confusion. CT scan shows a crescent-shaped hypodensity along the left hemisphere. What is the most likely diagnosis?", 
        options: [
          "Epidural Hematoma",
          "Subdural Hematoma",
          "Ischemic Stroke",
          "Brain Abscess"
        ],
        answer: "Subdural Hematoma"
      }
    ]
  },
  "104": {
    title: "Clinical Case-Based CT Brain Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. What is the most important indication for an immediate CT scan in a head injury patient?", 
        options: [
          "Glasgow Coma Scale (GCS) ≤8",
          "Mild headache without loss of consciousness",
          "Isolated scalp hematoma",
          "Mechanism of injury alone without symptoms"
        ],
        answer: "Glasgow Coma Scale (GCS) ≤8"
      },
      { 
        id: 2, 
        question: "Q2. What is the typical CT finding in an epidural hematoma?", 
        options: [
          "Biconvex (lentiform) hyperdense collection",
          "Crescent-shaped collection",
          "Crosses suture lines",
          "Commonly due to venous bleeding"
        ],
        answer: "Biconvex (lentiform) hyperdense collection"
      },
      { 
        id: 3, 
        question: "Q3. What is the hallmark CT appearance of a subdural hematoma?", 
        options: [
          "Always hyperdense regardless of age",
          "Biconvex shape",
          "Commonly arterial in origin",
          "Crescent-shaped hyperdense collection"
        ],
        answer: "Biconvex shape"
      },
      { 
        id: 4, 
        question: "Q4. Which CT findings indicate diffuse axonal injury (DAI)?", 
        options: [
          "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem",
          "Large single hematoma with midline shift",
          "Skull fracture with epidural hematoma",
          "Hydrocephalus"
        ],
        answer: "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem"
      },
      { 
        id: 5, 
        question: "Q5. When should a repeat CT scan be performed in a head injury patient?", 
        options: [
          "Neurological deterioration (e.g., drop in GCS, worsening weakness)",
          "Mild headache that is improving",
          "Stable patient with an initial small hematoma and no worsening symptoms",
          "No history of trauma but patient feels dizzy"
        ],
        answer: "Neurological deterioration (e.g., drop in GCS, worsening weakness)"
      }
    ]
  },
  "105": {
    title: "Clinical Case-Based CT Brain Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. What is the most important indication for an immediate CT scan in a head injury patient?", 
        options: [
          "Glasgow Coma Scale (GCS) ≤8",
          "Mild headache without loss of consciousness",
          "Isolated scalp hematoma",
          "Mechanism of injury alone without symptoms"
        ],
        answer: "Glasgow Coma Scale (GCS) ≤8"
      },
      { 
        id: 2, 
        question: "Q2. What is the typical CT finding in an epidural hematoma?", 
        options: [
          "Biconvex (lentiform) hyperdense collection",
          "Crescent-shaped collection",
          "Crosses suture lines",
          "Commonly due to venous bleeding"
        ],
        answer: "Biconvex (lentiform) hyperdense collection"
      },
      { 
        id: 3, 
        question: "Q3. What is the hallmark CT appearance of a subdural hematoma?", 
        options: [
          "Always hyperdense regardless of age",
          "Biconvex shape",
          "Commonly arterial in origin",
          "Crescent-shaped hyperdense collection"
        ],
        answer: "Biconvex shape"
      },
      { 
        id: 4, 
        question: "Q4. Which CT findings indicate diffuse axonal injury (DAI)?", 
        options: [
          "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem",
          "Large single hematoma with midline shift",
          "Skull fracture with epidural hematoma",
          "Hydrocephalus"
        ],
        answer: "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem"
      },
      { 
        id: 5, 
        question: "Q5. When should a repeat CT scan be performed in a head injury patient?", 
        options: [
          "Neurological deterioration (e.g., drop in GCS, worsening weakness)",
          "Mild headache that is improving",
          "Stable patient with an initial small hematoma and no worsening symptoms",
          "No history of trauma but patient feels dizzy"
        ],
        answer: "Neurological deterioration (e.g., drop in GCS, worsening weakness)"
      }
    ]
  },
  "106": {
    title: "Clinical Case-Based CT Brain Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. What is the most important indication for an immediate CT scan in a head injury patient?", 
        options: [
          "Glasgow Coma Scale (GCS) ≤8",
          "Mild headache without loss of consciousness",
          "Isolated scalp hematoma",
          "Mechanism of injury alone without symptoms"
        ],
        answer: "Glasgow Coma Scale (GCS) ≤8"
      },
      { 
        id: 2, 
        question: "Q2. What is the typical CT finding in an epidural hematoma?", 
        options: [
          "Biconvex (lentiform) hyperdense collection",
          "Crescent-shaped collection",
          "Crosses suture lines",
          "Commonly due to venous bleeding"
        ],
        answer: "Biconvex (lentiform) hyperdense collection"
      },
      { 
        id: 3, 
        question: "Q3. What is the hallmark CT appearance of a subdural hematoma?", 
        options: [
          "Always hyperdense regardless of age",
          "Biconvex shape",
          "Commonly arterial in origin",
          "Crescent-shaped hyperdense collection"
        ],
        answer: "Biconvex shape"
      },
      { 
        id: 4, 
        question: "Q4. Which CT findings indicate diffuse axonal injury (DAI)?", 
        options: [
          "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem",
          "Large single hematoma with midline shift",
          "Skull fracture with epidural hematoma",
          "Hydrocephalus"
        ],
        answer: "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem"
      },
      { 
        id: 5, 
        question: "Q5. When should a repeat CT scan be performed in a head injury patient?", 
        options: [
          "Neurological deterioration (e.g., drop in GCS, worsening weakness)",
          "Mild headache that is improving",
          "Stable patient with an initial small hematoma and no worsening symptoms",
          "No history of trauma but patient feels dizzy"
        ],
        answer: "Neurological deterioration (e.g., drop in GCS, worsening weakness)"
      }
    ]
  },
  "107": {
    title: "Clinical Case-Based CT Brain Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. What is the most important indication for an immediate CT scan in a head injury patient?", 
        options: [
          "Glasgow Coma Scale (GCS) ≤8",
          "Mild headache without loss of consciousness",
          "Isolated scalp hematoma",
          "Mechanism of injury alone without symptoms"
        ],
        answer: "Glasgow Coma Scale (GCS) ≤8"
      },
      { 
        id: 2, 
        question: "Q2. What is the typical CT finding in an epidural hematoma?", 
        options: [
          "Biconvex (lentiform) hyperdense collection",
          "Crescent-shaped collection",
          "Crosses suture lines",
          "Commonly due to venous bleeding"
        ],
        answer: "Biconvex (lentiform) hyperdense collection"
      },
      { 
        id: 3, 
        question: "Q3. What is the hallmark CT appearance of a subdural hematoma?", 
        options: [
          "Always hyperdense regardless of age",
          "Biconvex shape",
          "Commonly arterial in origin",
          "Crescent-shaped hyperdense collection"
        ],
        answer: "Biconvex shape"
      },
      { 
        id: 4, 
        question: "Q4. Which CT findings indicate diffuse axonal injury (DAI)?", 
        options: [
          "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem",
          "Large single hematoma with midline shift",
          "Skull fracture with epidural hematoma",
          "Hydrocephalus"
        ],
        answer: "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem"
      },
      { 
        id: 5, 
        question: "Q5. When should a repeat CT scan be performed in a head injury patient?", 
        options: [
          "Neurological deterioration (e.g., drop in GCS, worsening weakness)",
          "Mild headache that is improving",
          "Stable patient with an initial small hematoma and no worsening symptoms",
          "No history of trauma but patient feels dizzy"
        ],
        answer: "Neurological deterioration (e.g., drop in GCS, worsening weakness)"
      }
    ]
  },
  "108": {
    title: "Clinical Case-Based CT Brain Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. When is a contrast-enhanced CT (CECT) PNS required?",
        options: [
          "Suspected fungal sinusitis, tumors, or orbital complications",
          "Uncomplicated acute viral sinusitis",
          "Mild allergic rhinitis1",
          "Mechanism of injury alone without symptoms"
        ],
        answer: "Suspected fungal sinusitis, tumors, or orbital complications"
      },
      { 
        id: 2, 
        question: "Q2. What is the typical CT finding in an epidural hematoma?", 
        options: [
          "Biconvex (lentiform) hyperdense collection",
          "Crescent-shaped collection",
          "Crosses suture lines",
          "Commonly due to venous bleeding"
        ],
        answer: "Biconvex (lentiform) hyperdense collection"
      },
      { 
        id: 3, 
        question: "Q3. What is the hallmark CT appearance of a subdural hematoma?", 
        options: [
          "Always hyperdense regardless of age",
          "Biconvex shape",
          "Commonly arterial in origin",
          "Crescent-shaped hyperdense collection"
        ],
        answer: "Biconvex shape"
      },
      { 
        id: 4, 
        question: "Q4. Which CT findings indicate diffuse axonal injury (DAI)?", 
        options: [
          "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem",
          "Large single hematoma with midline shift",
          "Skull fracture with epidural hematoma",
          "Hydrocephalus"
        ],
        answer: "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem"
      },
      { 
        id: 5, 
        question: "Q5. When should a repeat CT scan be performed in a head injury patient?", 
        options: [
          "Neurological deterioration (e.g., drop in GCS, worsening weakness)",
          "Mild headache that is improving",
          "Stable patient with an initial small hematoma and no worsening symptoms",
          "No history of trauma but patient feels dizzy"
        ],
        answer: "Neurological deterioration (e.g., drop in GCS, worsening weakness)"
      }
    ]
  },
  "109": {
    title: "Clinical Case-Based CT Brain Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. What is the most important indication for an immediate CT scan in a head injury patient?", 
        options: [
          "Glasgow Coma Scale (GCS) ≤8",
          "Mild headache without loss of consciousness",
          "Isolated scalp hematoma",
          "Mechanism of injury alone without symptoms"
        ],
        answer: "Glasgow Coma Scale (GCS) ≤8"
      },
      { 
        id: 2, 
        question: "Q2. What is the typical CT finding in an epidural hematoma?", 
        options: [
          "Biconvex (lentiform) hyperdense collection",
          "Crescent-shaped collection",
          "Crosses suture lines",
          "Commonly due to venous bleeding"
        ],
        answer: "Biconvex (lentiform) hyperdense collection"
      },
      { 
        id: 3, 
        question: "Q3. What is the hallmark CT appearance of a subdural hematoma?", 
        options: [
          "Always hyperdense regardless of age",
          "Biconvex shape",
          "Commonly arterial in origin",
          "Crescent-shaped hyperdense collection"
        ],
        answer: "Biconvex shape"
      },
      { 
        id: 4, 
        question: "Q4. Which CT findings indicate diffuse axonal injury (DAI)?", 
        options: [
          "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem",
          "Large single hematoma with midline shift",
          "Skull fracture with epidural hematoma",
          "Hydrocephalus"
        ],
        answer: "Multiple small haemorrhages at gray-white junction, corpus callosum, and brainstem"
      },
      { 
        id: 5, 
        question: "Q5. When should a repeat CT scan be performed in a head injury patient?", 
        options: [
          "Neurological deterioration (e.g., drop in GCS, worsening weakness)",
          "Mild headache that is improving",
          "Stable patient with an initial small hematoma and no worsening symptoms",
          "No history of trauma but patient feels dizzy"
        ],
        answer: "Neurological deterioration (e.g., drop in GCS, worsening weakness)"
      }
    ]
  },
  "201": {
    title: "Clinical Case-Based CT Chest Quiz",
    description: "Test your diagnostic skills with real clinical scenarios",
    questions: [
      { 
        id: 1, 
        question: "Q1. When is a high-resolution CT (HRCT) Chest preferred over a routine CT Chest?",
        options: [
          "For assessing interstitial lung disease, fibrosis, and small airway disease",
          "For detecting large lung tumors",
          "Routine follow-up of lung nodules",
          "For evaluating pleural effusions"
        ],
        answer: "For assessing interstitial lung disease, fibrosis, and small airway disease"
      },
      { 
        id: 2, 
        question: "Q2. What is the ideal patient positioning for a CT Chest scan?", 
        options: [
          "Supine with arms raised above the head",
          "Prone with head flexed",
          "Sitting upright with arms crossed",
          "Lateral decubitus position"
        ],
        answer: "Supine with arms raised above the head"
      },
      { 
        id: 3, 
        question: "Q3. Which of the following is an advantage of a non-contrast CT Chest over a contrast-enhanced CT?", 
        options: [
          "Better evaluation of lung parenchyma and airways",
          "Better detection of vascular structures",
          "Ideal for assessing mediastinal lymph nodes",
          "Always preferred for pulmonary embolism diagnosis"
        ],
        answer: "Better evaluation of lung parenchyma and airways"
      },
      { 
        id: 4, 
        question: "Q4. What is a key finding in CT Chest suggestive of COVID-19", 
        options: [
          "Bilateral ground-glass opacities (GGOs) with or without consolidation",
          "Single large lung cavity",
          "Unilateral pleural effusion",
          "Normal lung parenchyma"
        ],
        answer: "Bilateral ground-glass opacities (GGOs) with or without consolidation"
      },
      { 
        id: 5, 
        question: "Q5. When is a high-resolution CT (HRCT) Chest preferred over a routine CT Chest?", 
        options: [
          "For assessing interstitial lung disease, fibrosis, and small airway disease",
          "For detecting large lung tumors",
          "Routine follow-up of lung nodules",
          "For evaluating pleural effusions"
        ],
        answer: "For assessing interstitial lung disease, fibrosis, and small airway disease"
      }
    ]
  },
  "202": {
    title: "CT Thorax (Chest) Contrast Study",
    description: "Test your knowledge about contrast-enhanced CT of the chest.",
    questions: [
      {
        id: 1,
        question: "Q1. What are the main indications for a contrast-enhanced CT (CECT) Chest?",
        options: [
          " Pulmonary embolism (PE) evaluation",
          " Mediastinal lymphadenopathy assessment",
          " Lung mass or tumor characterization",
          " Routine screening for lung infections"
        ],
        answer: [" Pulmonary embolism (PE) evaluation", " Mediastinal lymphadenopathy assessment", " Lung mass or tumor characterization"]
      },
      {
        id: 2,
        question: "Q2. Which factors should be considered before administering IV contrast for a CT Chest?",
        options: [
          " Renal function (Serum creatinine & eGFR)",
          " History of contrast allergy",
          " Presence of metallic implants",
          " Patient's blood pressure"
        ],
        answer: [" Renal function (Serum creatinine & eGFR)", " History of contrast allergy", " Presence of metallic implants"]
      },
      {
        id: 3,
        question: "Q3. What is the preferred contrast phase for evaluating a pulmonary embolism (PE) in CT Chest?",
        options: [
          " Pulmonary arterial phase (timed for peak enhancement in pulmonary arteries)",
          " Delayed phase",
          " Non-contrast phase",
          " Venous phase"
        ],
        answer: " Pulmonary arterial phase (timed for peak enhancement in pulmonary arteries)"
      },
      {
        id: 4,
        question: "Q4. Which findings on a contrast-enhanced CT Chest suggest malignancy?",
        options: [
          "Irregular or spiculated lung nodule margins",
          "Enhancement of lung mass >15 HU after contrast injection",
          "Necrotic lymph nodes with rim enhancement",
          "Smooth, well-defined lung nodule with no enhancement"
        ],
        answer: ["Irregular or spiculated lung nodule margins", " Enhancement of lung mass >15 HU after contrast injection", " Necrotic lymph nodes with rim enhancement"]
      },
      {
        id: 5,
        question: "Q5. What are possible complications of IV contrast administration in CT Chest?",
        options: [
          "Contrast-induced nephropathy (CIN)",
          "Anaphylactic reaction ",
          "Extravasation at injection site",
          "Pulmonary hypertension"
        ],
        answer: [" Contrast-induced nephropathy (CIN)", " Anaphylactic reaction ", " Extravasation at injection site"]
      }
    ]
  },
  "203": {
    title: "CT HRCT (Chest) Contrast Study",
    description: "Test your knowledge about contrast-enhanced CT of the chest.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary indication for performing an HRCT Chest?",
        options: [
          " Pulmonary embolism diagnosis",
          " Interstitial lung disease (ILD) assessment",
          " Large lung mass evaluation",
          " Pleural effusion assessment"
        ],
        answer: [" Interstitial lung disease (ILD) assessment"]
      },
      {
        id: 2,
        question: "Q2. What is the classic HRCT finding in idiopathic pulmonary fibrosis (IPF)?",
        options: [
          " Honeycombing in the subpleural and basal lung regions ",
          " Diffuse ground-glass opacities without fibrosiS",
          " Central lung consolidation",
          " Multiple pulmonary nodules"
        ],
        answer: [" Honeycombing in the subpleural and basal lung regions "]
      },
      {
        id: 3,
        question: "Q3. How does HRCT differ from routine CT Chest?",
        options: [
          " Uses thin-section slices (≤1.25 mm) for better lung detail ",
          " Requires IV contrast for better resolution",
          " Better for detecting large lung masses",
          " Uses thicker slices for improved spatial resolution"
        ],
        answer: " Uses thin-section slices (≤1.25 mm) for better lung detail "
      },
      {
        id: 4,
        question: "Q4. Which of the following HRCT findings is suggestive of hypersensitivity pneumonitis (HP)?",
        options: [
          "Centrilobular ground-glass nodules with mosaic attenuation",
          "Honeycombing in the lower lobes",
          "Large solitary lung mass",
          "Isolated pleural effusion"
        ],
        answer: ["Centrilobular ground-glass nodules with mosaic attenuation"]
      },
      {
        id: 5,
        question: "Q5. When is expiratory phase imaging useful in HRCT?",
        options: [
          "To assess air trapping in small airway diseases",
          "To improve lung mass detection ",
          "To evaluate pleural effusions",
          "To enhance vascular structures"
        ],
        answer: [" To assess air trapping in small airway diseases"]
      }
    ]
  },
  "204": {
    title: "CT Lung Cancer Screening",
    description: "Test your knowledge about contrast-enhanced CT of the chest.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary purpose of CT Coronary Artery Calcium Scoring?",
        options: [
          " Detecting coronary artery stenosis",
          " Assessing the risk of coronary artery disease (CAD) based on calcified plaque burden",
          " Evaluating myocardial perfusion",
          " Diagnosing acute coronary syndrome"
        ],
        answer: [" Assessing the risk of coronary artery disease (CAD) based on calcified plaque burden"]
      },
      {
        id: 2,
        question: "Q2. Which imaging technique is used for CAC scoring?",
        options: [
          " Non-contrast, ECG-gated CT scan",
          " Contrast-enhanced CT angiography",
          " MRI of the heart",
          " Stress echocardiography"
        ],
        answer: [" Non-contrast, ECG-gated CT scan"]
      },
      {
        id: 3,
        question: "Q3. What is considered a high coronary artery calcium score indicating a high risk of cardiovascular events?",
        options: [
          " A)  (No risk)",
          " 1–99 (Mild risk)",
          " C) ≥400 (High risk) ",
          "D) 100–399 (Moderate risk)"
        ],
        answer: [" A)  (No risk)"]
      },
      {
        id: 4,
        question: "Q4. In which of the following patients is CT Coronary Calcium Scoring most useful?",
        options: [
          "Asymptomatic individuals with intermediate cardiovascular risk (based on clinical risk factors)",
          "Patients with ongoing chest pain suggestive of a heart attack",
          "Young adults with no cardiovascular risk factors",
          "Patients with known severe coronary artery disease"
        ],
        answer: ["Asymptomatic individuals with intermediate cardiovascular risk (based on clinical risk factors)"]
      },
      {
        id: 5, 
        question: "Q5. What are the limitations of CAC scoring?",
        options: [
          "Does not assess non-calcified (soft) plaques that can still cause heart attacks",
          "Cannot measure the degree of coronary artery stenosis ",
          "Requires contrast injection",
          "Can replace coronary angiography in diagnosing obstructive CAD"
        ],
        answer: ["Does not assess non-calcified (soft) plaques that can still cause heart attacks", "Cannot measure the degree of coronary artery stenosis "]
      }
    ]
  },
  "205": {
    title: "CT Coronary Artery Calcium Scoring",
    description: "Test your knowledge about contrast-enhanced CT of the chest.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary purpose of CT Coronary Artery Calcium Scoring?",
        options: [
          " Detecting coronary artery stenosis",
          " Assessing the risk of coronary artery disease (CAD) based on calcified plaque burden",
          " Evaluating myocardial perfusion",
          " Diagnosing acute coronary syndrome"
        ],
        answer: [" Assessing the risk of coronary artery disease (CAD) based on calcified plaque burden"]
      },
      {
        id: 2,
        question: "Q2. Which imaging technique is used for CAC scoring?",
        options: [
          " Non-contrast, ECG-gated CT scan",
          " Contrast-enhanced CT angiography",
          " MRI of the heart",
          " Stress echocardiography"
        ],
        answer: [" Non-contrast, ECG-gated CT scan"]
      },
      {
        id: 3,
        question: "Q3. What is considered a high coronary artery calcium score indicating a high risk of cardiovascular events?",
        options: [
          " A)  (No risk)",
          " 1–99 (Mild risk)",
          " C) ≥400 (High risk) ",
          "D) 100–399 (Moderate risk)"
        ],
        answer: [" A)  (No risk)"]
      },
      {
        id: 4,
        question: "Q4. In which of the following patients is CT Coronary Calcium Scoring most useful?",
        options: [
          "Asymptomatic individuals with intermediate cardiovascular risk (based on clinical risk factors)",
          "Patients with ongoing chest pain suggestive of a heart attack",
          "Young adults with no cardiovascular risk factors",
          "Patients with known severe coronary artery disease"
        ],
        answer: ["Asymptomatic individuals with intermediate cardiovascular risk (based on clinical risk factors)"]
      },
      {
        id: 5, 
        question: "Q5. What are the limitations of CAC scoring?",
        options: [
          "Does not assess non-calcified (soft) plaques that can still cause heart attacks",
          "Cannot measure the degree of coronary artery stenosis ",
          "Requires contrast injection",
          "Can replace coronary angiography in diagnosing obstructive CAD"
        ],
        answer: ["Does not assess non-calcified (soft) plaques that can still cause heart attacks", "Cannot measure the degree of coronary artery stenosis "]
      }
    ]
  },
  "301": {
    title: "CT Abdomen Plain Study",
    description: "Test your knowledge about CT abdomen and pelvis plain study.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary purpose of a CT abdomen and pelvis plain study?",
        options: [
          "To evaluate liver function",
          "To visualize bone fractures",
          "To assess abdominal and pelvic organs for abnormalities",
          "To assess lung pathology"
        ],
        answer: "To assess abdominal and pelvic organs for abnormalities"
      },
      {
        id: 2,
        question: "Q2. Which of the following is NOT typically evaluated in a CT abdomen and pelvis plain study?",
        options: [
          "Liver",
          "Kidneys",
          "Bladder",
          "Dorsal Spine fractures"
        ],
        answer: "Dorsal Spine fractures"
      },
      {
        id: 3,
        question: "Q3. What is the primary imaging modality for detecting acute appendicitis?",
        options: [
          "Ultrasound",
          "MRI",
          "CT abdomen and pelvis plain study",
          "X-ray"
        ],
        answer: "CT abdomen and pelvis plain study"
      },
      {
        id: 4,
        question: "Q4. Which contrast is used in a CT abdomen and pelvis plain study?",
        options: [
          "Oral contrast only",
          "Intravenous (IV) contrast only",
          "Both oral and intravenous (IV) contrast",
          "No contrast"
        ],
        answer: "No contrast"
      },
      {
        id: 5,
        question: "Q5. Which of the following is a common indication for a CT abdomen and pelvis plain study?",
        options: [
          "Suspected bowel obstruction",
          "Evaluation of uterine fibroids",
          "To monitor chemotherapy response",
          "All of the above"
        ],
        answer: "All of the above"
      },
      {
        id: 6,
        question: "Q6. What is the typical patient preparation for a CT abdomen and pelvis plain study?",
        options: [
          "Fasting for at least 6 hours",
          "Taking a contrast agent orally",
          "Drinking a large amount of water",
          "No special preparation needed"
        ],
        answer: "Fasting for at least 6 hours"
      },
      {
        id: 7,
        question: "Q7. Which of the following structures can be visualized using a CT abdomen and pelvis plain study?",
        options: [
          "Liver",
          "Spleen",
          "Pancreas",
          "All of the above"
        ],
        answer: "All of the above"
      },
      {
        id: 8,
        question: "Q8. What is the common radiation dose for a CT abdomen and pelvis plain study?",
        options: [
          "1-2 mSv",
          "5-10 mSv",
          "10-20 mSv",
          "30-50 mSv"
        ],
        answer: "5-10 mSv"
      },
      {
        id: 9,
        question: "Q9. Which of the following is NOT a contraindication for a CT abdomen and pelvis plain study?",
        options: [
          "Pregnancy",
          "Severe obesity",
          "Active gastrointestinal bleeding",
          "Severe renal impairment"
        ],
        answer: "Pregnancy"
      },
      {
        id: 10,
        question: "Q10. What is a common finding on a CT abdomen and pelvis plain study in patients with diverticulitis?",
        options: [
          "Pericolonic fat stranding",
          "Bowel perforation",
          "Abscess formation",
          "All of the above"
        ],
        answer: "All of the above"
      },
      {
        id: 11,
        question: "Q11. Which organ is most commonly affected by cysts that may be visualized on a CT abdomen and pelvis plain study?",
        options: [
          "Spleen",
          "Kidneys",
          "Liver",
          "Pancreas"
        ],
        answer: "Kidneys"
      },
      {
        id: 12,
        question: "Q12. What is the main limitation of a CT abdomen and pelvis plain study?",
        options: [
          "It cannot visualize soft tissue structures well",
          "It provides less detailed images of bones compared to X-ray",
          "It cannot detect small bowel pathology effectively",
          "It involves high radiation exposure"
        ],
        answer: "It involves high radiation exposure"
      },
      {
        id: 13,
        question: "Q13. What can be used to evaluate for a suspected renal stone in a CT abdomen and pelvis plain study?",
        options: [
          "Intravenous pyelogram",
          "Plain CT without contrast",
          "Oral contrast administration",
          "MRI"
        ],
        answer: "Plain CT without contrast"
      },
      {
        id: 14,
        question: "Q14. Which of the following would likely NOT require a CT abdomen and pelvis plain study?",
        options: [
          "Abdominal trauma with suspected internal bleeding",
          "Unexplained abdominal pain with suspected malignancy",
          "Routine screening for colon cancer",
          "Suspected kidney stones"
        ],
        answer: "Routine screening for colon cancer"
      },
      {
        id: 15,
        question: "Q15. What is a typical feature of bowel obstruction on a CT abdomen and pelvis plain study?",
        options: [
          "Dilated loops of bowel",
          "Increased peristalsis",
          "Thickened bowel wall",
          "All of the above"
        ],
        answer: "Dilated loops of bowel"
      },
      {
        id: 16,
        question: "Q16. Which of the following conditions can be detected using a CT abdomen and pelvis plain study?",
        options: [
          "Acute pancreatitis",
          "Cholecystitis",
          "Abdominal aortic aneurysm",
          "All of the above"
        ],
        answer: "All of the above"
      },
      {
        id: 17,
        question: "Q17. What type of CT scan is typically performed for evaluating a suspected bowel perforation?",
        options: [
          "CT with oral contrast",
          "CT without contrast",
          "CT with intravenous contrast",
          "CT with both oral and intravenous contrast"
        ],
        answer: "CT with intravenous contrast"
      },
      {
        id: 18,
        question: "Q18. What type of image artifact can occur with a CT abdomen and pelvis plain study?",
        options: [
          "Motion artifact",
          "Metal artifact",
          "Partial volume artifact",
          "All of the above"
        ],
        answer: "All of the above"
      },
      {
        id: 19,
        question: "Q19. Which of the following abdominal pathologies is more likely to be missed on a plain CT abdomen and pelvis study?",
        options: [
          "Renal stones",
          "Pancreatic cancer",
          "Intestinal obstruction",
          "Cholelithiasis"
        ],
        answer: "Pancreatic cancer"
      },
      {
        id: 20,
        question: "Q20. What is the main role of CT abdomen and pelvis plain studies in trauma cases?",
        options: [
          "To detect bony fractures",
          "To assess the abdominal cavity for bleeding or organ injury",
          "To evaluate the pelvic organs",
          "To assess for soft tissue injuries"
        ],
        answer: "To assess the abdominal cavity for bleeding or organ injury"
      },
      {
        id: 21,
        question: "Q21. What is the ideal imaging technique for evaluating soft tissue structures like the liver or pancreas?",
        options: [
          "Plain X-ray",
          "Ultrasound",
          "CT with contrast",
          "MRI"
        ],
        answer: "CT with contrast"
      },
      {
        id: 22,
        question: "Q22. How does a CT abdomen and pelvis plain study help in the diagnosis of an abdominal aortic aneurysm?",
        options: [
          "It detects calcification in the aortic wall",
          "It visualizes the size and shape of the aneurysm",
          "It provides information on blood flow",
          "It identifies aortic dissection"
        ],
        answer: "It visualizes the size and shape of the aneurysm"
      },
      {
        id: 23,
        question: "Q23. In what scenario would a CT abdomen and pelvis plain study NOT be sufficient for diagnosis?",
        options: [
          "Suspected bowel perforation",
          "Renal stones",
          "Suspected mesenteric ischemia",
          "Severe trauma with internal bleeding"
        ],
        answer: "Suspected mesenteric ischemia"
      },
      {
        id: 24,
        question: "Q24. What is a typical finding on a CT abdomen and pelvis plain study for a patient with a history of chronic alcohol use?",
        options: [
          "Pancreatic calcifications",
          "Liver cirrhosis",
          "Splenomegaly",
          "All of the above"
        ],
        answer: "All of the above"
      },
      {
        id: 25,
        question: "Q25. What is the role of CT abdomen and pelvis plain study in the evaluation of bowel ischemia?",
        options: [
          "It helps visualize bowel wall thickening",
          "It provides an assessment of bowel motility",
          "It identifies areas of bowel infarction",
          "It assesses blood flow in mesenteric arteries"
        ],
        answer: "It helps visualize bowel wall thickening"
      },
      {
        id: 26,
        question: "Q26. Which of the following findings is suggestive of a liver mass on a CT abdomen and pelvis plain study?",
        options: [
          "Well-defined hypodense area in the liver",
          "Calcified lesion in the liver",
          "Enlarged gallbladder",
          "Thickened gastric wall"
        ],
        answer: "Well-defined hypodense area in the liver"
      },
      {
        id: 27,
        question: "Q27. Which of the following is NOT typically seen in a CT abdomen and pelvis plain study in patients with Crohn's disease?",
        options: [
          "Bowel wall thickening",
          "Mesenteric fat stranding",
          "Bowel perforation",
          "Hepatic steatosis"
        ],
        answer: "Hepatic steatosis"
      },
      {
        id: 28,
        question: "Q28. What can a CT abdomen and pelvis plain study reveal in a patient with suspected endometriosis?",
        options: [
          "Uterine fibroids",
          "Endometrial cysts",
          "Pelvic adhesions",
          "All of the above"
        ],
        answer: "Endometrial cysts"
      },
      {
        id: 29,
        question: "Q29. Which of the following conditions can be assessed using a CT abdomen and pelvis plain study in a patient with right lower quadrant pain?",
        options: [
          "Appendicitis",
          "Ovarian cyst rupture",
          "Ectopic pregnancy",
          "All of the above"
        ],
        answer: "All of the above"
      },
      {
        id: 30,
        question: "Q30. What can a CT abdomen and pelvis plain study help identify in a patient with suspected inflammatory bowel disease (IBD)?",
        options: [
          "Wall thickening of the small and large intestine",
          "Fistulas and abscesses",
          "Mesenteric lymphadenopathy",
          "All of the above"
        ],
        answer: "All of the above"
      }
    ]
  },
  "302": {
    title: "CT Abdomen Contrast Study",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary purpose of a CT abdomen and pelvis contrast study?",
        options: [
          "To visualize bone fractures",
          "To assess soft tissue and vascular structures in the abdomen and pelvis",
          "To evaluate lung pathology",
          "To detect gastrointestinal motility disorders"
        ],
        answer: "To assess soft tissue and vascular structures in the abdomen and pelvis"
      },
      {
        id: 2,
        question: "Q2. Why is oral contrast sometimes used in a CT abdomen study?",
        options: [
          "To enhance visualization of blood vessels",
          "To improve differentiation of gastrointestinal structures",
          "To diagnose fractures",
          "To detect air embolism"
        ],
        answer: "To improve differentiation of gastrointestinal structures"
      },
      {
        id: 3,
        question: "Q3. Which condition requires IV contrast for optimal evaluation?",
        options: [
          "Kidney stones",
          "Liver metastases",
          "Pneumoperitoneum",
          "Free air under the diaphragm"
        ],
        answer: "Liver metastases"
      },
      {
        id: 4,
        question: "Q4. How long should a patient fast before a CT abdomen with contrast?",
        options: [
          "No fasting required",
          "1 hour",
          "4-6 hours",
          "24 hours"
        ],
        answer: "4-6 hours"
      },
      {
        id: 5,
        question: "Q5. Which patient should NOT receive IV contrast?",
        options: [
          "A patient with a history of mild iodine allergy",
          "A patient with GFR < 30 mL/min",
          "A patient with controlled diabetes",
          "A patient with suspected bowel obstruction"
        ],
        answer: "A patient with GFR < 30 mL/min"
      },
      {
        id: 6,
        question: "Q6. What is the primary risk of IV contrast administration?",
        options: [
          "Pulmonary embolism",
          "Hyperglycemia",
          "Nephrotoxicity",
          "Dehydration"
        ],
        answer: "Nephrotoxicity"
      },
      {
        id: 7,
        question: "Q7. What should be done before administering IV contrast in a patient with chronic kidney disease?",
        options: [
          "Check blood glucose levels",
          "Obtain a renal function test (creatinine, GFR)",
          "Give diuretics",
          "Perform an ECG"
        ],
        answer: "Obtain a renal function test (creatinine, GFR)"
      },
      {
        id: 8,
        question: "Q8. Which of the following is a contraindication to IV contrast?",
        options: [
          "Mild hypertension",
          "Severe contrast allergy",
          "History of gallstones",
          "Low potassium levels"
        ],
        answer: "Severe contrast allergy"
      },
      {
        id: 9,
        question: "Q9. Which medication may be given before a CT scan if a patient has a mild iodine contrast allergy?",
        options: [
          "Insulin",
          "Prednisolone or Methylprednisolone",
          "Metformin",
          "Epinephrine"
        ],
        answer: "Prednisolone or Methylprednisolone"
      },
      {
        id: 10,
        question: "Q10. What is the primary route of contrast excretion?",
        options: [
          "Liver",
          "Lungs",
          "Kidneys",
          "Small intestine"
        ],
        answer: "Kidneys"
      },
      {
        id: 11,
        question: "Q11. What is the typical IV contrast dose for an adult CT abdomen study?",
        options: [
          "10 mL",
          "50-150 mL",
          "300 mL",
          "500 mL"
        ],
        answer: "50-150 mL"
      },
      {
        id: 12,
        question: "Q12. What is the standard IV contrast flow rate?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 13,
        question: "Q13. Which contrast phase is best for detecting liver lesions?",
        options: [
          "Non-contrast",
          "Arterial phase",
          "Portal venous phase",
          "Delayed phase"
        ],
        answer: "Portal venous phase"
      },
      {
        id: 14,
        question: "Q14. What is the primary advantage of using dual-phase contrast imaging?",
        options: [
          "Reduced radiation exposure",
          "Improved visualization of vascular and parenchymal structures",
          "Less contrast agent required",
          "Faster scan time"
        ],
        answer: "Improved visualization of vascular and parenchymal structures"
      },
      {
        id: 15,
        question: "Q15. If a patient cannot drink oral contrast, what alternative can be used?",
        options: [
          "Normal saline",
          "IV contrast only",
          "Rectal contrast",
          "No contrast at all"
        ],
        answer: "Rectal contrast"
      },
      {
        id: 16,
        question: "Q16. What finding on a CT abdomen with contrast is most suggestive of appendicitis?",
        options: [
          "Diffuse bowel thickening",
          "Non-enhancing renal cyst",
          "Dilated appendix with surrounding fat stranding",
          "Air-fluid levels in the stomach"
        ],
        answer: "Dilated appendix with surrounding fat stranding"
      },
      {
        id: 17,
        question: "Q17. A \"target sign\" in a contrast-enhanced CT of the abdomen is associated with:",
        options: [
          "Bowel ischemia",
          "Pancreatitis",
          "Gallstones",
          "Kidney cysts"
        ],
        answer: "Bowel ischemia"
      },
      {
        id: 18,
        question: "Q18. A hyperdense lesion in the portal venous phase suggests:",
        options: [
          "Hepatic hemangioma",
          "Simple liver cyst",
          "Liver metastasis",
          "Portal vein thrombosis"
        ],
        answer: "Liver metastasis"
      },
      {
        id: 19,
        question: "Q19. What is the most common finding in acute pancreatitis on contrast-enhanced CT?",
        options: [
          "Pancreatic calcifications",
          "Fatty infiltration",
          "Peripancreatic fluid collection",
          "Hepatomegaly"
        ],
        answer: "Peripancreatic fluid collection"
      },
      {
        id: 20,
        question: "Q20. A contrast-enhancing renal mass on a CT scan is concerning for:",
        options: [
          "Renal cell carcinoma",
          "Simple cyst",
          "Ureteral stone",
          "Pyelonephritis"
        ],
        answer: "Renal cell carcinoma"
      },
      {
        id: 21,
        question: "Q21. What is a potential complication of IV contrast extravasation?",
        options: [
          "Deep vein thrombosis",
          "Soft tissue necrosis",
          "Pneumothorax",
          "Hematuria"
        ],
        answer: "Soft tissue necrosis"
      },
      {
        id: 22,
        question: "Q22. How can motion artifacts be minimized during a CT abdomen scan?",
        options: [
          "Increasing the scan time",
          "Using a higher radiation dose",
          "Coaching the patient on breath-holding",
          "Performing the scan without contrast"
        ],
        answer: "Coaching the patient on breath-holding"
      },
      {
        id: 23,
        question: "Q23. Which artifact may occur due to high-density contrast pooling?",
        options: [
          "Beam-hardening artifact",
          "Ring artifact",
          "Motion artifact",
          "Partial volume artifact"
        ],
        answer: "Beam-hardening artifact"
      },
      {
        id: 24,
        question: "Q24. Which of the following can reduce contrast nephropathy risk?",
        options: [
          "Increasing contrast dose",
          "Hydration with IV fluids",
          "Using MRI instead of CT",
          "Administering epinephrine before contrast"
        ],
        answer: "Hydration with IV fluids"
      },
      {
        id: 25,
        question: "Q25. Why might a non-contrast phase be performed in addition to contrast-enhanced phases?",
        options: [
          "To reduce patient radiation exposure",
          "To evaluate for calcifications or kidney stones",
          "To save scan time",
          "To minimize contrast side effects"
        ],
        answer: "To evaluate for calcifications or kidney stones"
      },
      {
        id: 26,
        question: "Q26. What is the most common side effect of IV contrast administration?",
        options: [
          "Anaphylaxis",
          "Mild nausea or warmth sensation",
          "Seizures",
          "Severe hypotension"
        ],
        answer: "Mild nausea or warmth sensation"
      },
      {
        id: 27,
        question: "Q27. What should be done if a patient experiences mild hives after contrast administration?",
        options: [
          "Monitor and consider an antihistamine if symptoms worsen",
          "Immediately stop the scan and administer epinephrine",
          "Send the patient home without follow-up",
          "Avoid using contrast in future studies"
        ],
        answer: "Monitor and consider an antihistamine if symptoms worsen"
      },
      {
        id: 28,
        question: "Q28. In a pregnant patient, when is a CT abdomen with contrast recommended?",
        options: [
          "Always safe, no concerns",
          "Only if the benefits outweigh the risks",
          "Never",
          "Only in the third trimester"
        ],
        answer: "Only if the benefits outweigh the risks"
      },
      {
        id: 29,
        question: "Q29. Why should metformin be temporarily stopped after IV contrast administration?",
        options: [
          "It can cause lactic acidosis in patients with renal impairment",
          "It interacts with contrast and causes false imaging results",
          "It enhances the effect of IV contrast",
          "It causes acute pancreatitis"
        ],
        answer: "It can cause lactic acidosis in patients with renal impairment"
      },
      {
        id: 30,
        question: "Q30. What is the best imaging modality for evaluating suspected gallstones if ultrasound is inconclusive?",
        options: [
          "X-ray",
          "CT without contrast",
          "MRI/MRCP",
          "Fluoroscopy"
        ],
        answer: "MRI/MRCP"
      }
    ]
  },
  "303": {
    title: "CT Liver Study",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary indication for CT enterography?",
        options: [
          "Liver cirrhosis",
          "Small bowel disorders (e.g., Crohn’s disease)",
          "Pulmonary embolism",
          "Kidney stones"
        ],
        answer: "Small bowel disorders (e.g., Crohn’s disease)"
      },
      {
        id: 2,
        question: "Q2. Which oral contrast is preferred for CT enterography?",
        options: [
          "Iodinated contrast",
          "Low-density (neutral) contrast like VoLumen",
          "Barium sulphate",
          "No oral contrast is needed"
        ],
        answer: "Low-density (neutral) contrast like VoLumen"
      },
      {
        id: 3,
        question: "Q3. Why is neutral oral contrast used in CT enterography?",
        options: [
          "To enhance visualization of the colon",
          "To distend the small bowel for better mucosal evaluation",
          "To improve detection of liver lesions",
          "To reduce radiation exposure"
        ],
        answer: "To distend the small bowel for better mucosal evaluation"
      },
      {
        id: 4,
        question: "Q4. How long should a patient fast before a CT enterography?",
        options: [
          "No fasting required",
          "1 hour",
          "4-6 hours",
          "24 hours"
        ],
        answer: "4-6 hours"
      },
      {
        id: 5,
        question: "Q5. Which condition is best evaluated with CT enterography?",
        options: [
          "Hepatic steatosis",
          "Crohn’s disease",
          "Gallstones",
          "Appendicitis"
        ],
        answer: "Crohn’s disease"
      },
      {
        id: 6,
        question: "Q6. What is the recommended volume of oral contrast for CT enterography?",
        options: [
          "100 mL",
          "1000-1500 mL ",
          "2000-2500 mL",
          "5000 mL"
        ],
        answer: "1000-1500 mL"
      },
      {
        id: 7,
        question: "Q7. How long before scanning should the patient start drinking the oral contrast?",
        options: [
          "45-60 minutes before scanning",
          "5 minutes before scanning",
          "Immediately before scanning",
          "No oral contrast is needed"
        ],
        answer: "45-60 minutes before scanning"
      },
      {
        id: 8,
        question: "Q8. What is the typical IV contrast dose for CT enterography?",
        options: [
          "10 mL",
          "50 mL",
          "100-150 mL",
          "500 mL"
        ],
        answer: "100-150 mL"
      },
      {
        id: 9,
        question: "Q9. What is the recommended IV contrast flow rate for CT enterography?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 10,
        question: "Q10. Which phase is most important for evaluating active inflammation in the small bowel?",
        options: [
          "Enteric phase (50-60 seconds post-injection)",
          "Non-contrast phase",
          "Arterial phase",
          "Delayed phase"
        ],
        answer: "Enteric phase (50-60 seconds post-injection)"
      },
      {
        id: 11,
        question: "Q11. Why is IV contrast necessary in CT enterography?",
        options: [
          "To evaluate lung parenchyma",
          "To differentiate enhancing bowel walls from non-enhancing pathology",
          "To assess for kidney stones",
          "To reduce radiation exposure"
        ],
        answer: "To differentiate enhancing bowel walls from non-enhancing pathology"
      },
      {
        id: 12,
        question: "Q12. What is the standard IV contrast flow rate?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 13,
        question: "Q13. Which contrast phase is best for detecting liver lesions?",
        options: [
          "Non-contrast",
          "Arterial phase",
          "Portal venous phase",
          "Delayed phase"
        ],
        answer: "Portal venous phase"
      },
      {
        id: 14,
        question: "Q14. What is the primary advantage of using dual-phase contrast imaging?",
        options: [
          "Reduced radiation exposure",
          "Improved visualization of vascular and parenchymal structures",
          "Less contrast agent required",
          "Faster scan time"
        ],
        answer: "Improved visualization of vascular and parenchymal structures"
      },
      {
        id: 15,
        question: "Q15. If a patient cannot drink oral contrast, what alternative can be used?",
        options: [
          "Normal saline",
          "IV contrast only",
          "Rectal contrast",
          "No contrast at all"
        ],
        answer: "Rectal contrast"
      },
      {
        id: 16,
        question: "Q16. What finding on a CT abdomen with contrast is most suggestive of appendicitis?",
        options: [
          "Diffuse bowel thickening",
          "Non-enhancing renal cyst",
          "Dilated appendix with surrounding fat stranding",
          "Air-fluid levels in the stomach"
        ],
        answer: "Dilated appendix with surrounding fat stranding"
      },
      {
        id: 17,
        question: "Q17. A \"target sign\" in a contrast-enhanced CT of the abdomen is associated with:",
        options: [
          "Bowel ischemia",
          "Pancreatitis",
          "Gallstones",
          "Kidney cysts"
        ],
        answer: "Bowel ischemia"
      },
      {
        id: 18,
        question: "Q18. A hyperdense lesion in the portal venous phase suggests:",
        options: [
          "Hepatic hemangioma",
          "Simple liver cyst",
          "Liver metastasis",
          "Portal vein thrombosis"
        ],
        answer: "Liver metastasis"
      },
      {
        id: 19,
        question: "Q19. What is the most common finding in acute pancreatitis on contrast-enhanced CT?",
        options: [
          "Pancreatic calcifications",
          "Fatty infiltration",
          "Peripancreatic fluid collection",
          "Hepatomegaly"
        ],
        answer: "Peripancreatic fluid collection"
      },
      {
        id: 20,
        question: "Q20. A contrast-enhancing renal mass on a CT scan is concerning for:",
        options: [
          "Renal cell carcinoma",
          "Simple cyst",
          "Ureteral stone",
          "Pyelonephritis"
        ],
        answer: "Renal cell carcinoma"
      },
      {
        id: 21,
        question: "Q21. What is a potential complication of IV contrast extravasation?",
        options: [
          "Deep vein thrombosis",
          "Soft tissue necrosis",
          "Pneumothorax",
          "Hematuria"
        ],
        answer: "Soft tissue necrosis"
      },
      {
        id: 22,
        question: "Q22. How can motion artifacts be minimized during a CT abdomen scan?",
        options: [
          "Increasing the scan time",
          "Using a higher radiation dose",
          "Coaching the patient on breath-holding",
          "Performing the scan without contrast"
        ],
        answer: "Coaching the patient on breath-holding"
      },
      {
        id: 23,
        question: "Q23. Which artifact may occur due to high-density contrast pooling?",
        options: [
          "Beam-hardening artifact",
          "Ring artifact",
          "Motion artifact",
          "Partial volume artifact"
        ],
        answer: "Beam-hardening artifact"
      },
      {
        id: 24,
        question: "Q24. Which of the following can reduce contrast nephropathy risk?",
        options: [
          "Increasing contrast dose",
          "Hydration with IV fluids",
          "Using MRI instead of CT",
          "Administering epinephrine before contrast"
        ],
        answer: "Hydration with IV fluids"
      },
      {
        id: 25,
        question: "Q25. Why might a non-contrast phase be performed in addition to contrast-enhanced phases?",
        options: [
          "To reduce patient radiation exposure",
          "To evaluate for calcifications or kidney stones",
          "To save scan time",
          "To minimize contrast side effects"
        ],
        answer: "To evaluate for calcifications or kidney stones"
      },
      {
        id: 26,
        question: "Q26. What is the most common side effect of IV contrast administration?",
        options: [
          "Anaphylaxis",
          "Mild nausea or warmth sensation",
          "Seizures",
          "Severe hypotension"
        ],
        answer: "Mild nausea or warmth sensation"
      },
      {
        id: 27,
        question: "Q27. What should be done if a patient experiences mild hives after contrast administration?",
        options: [
          "Monitor and consider an antihistamine if symptoms worsen",
          "Immediately stop the scan and administer epinephrine",
          "Send the patient home without follow-up",
          "Avoid using contrast in future studies"
        ],
        answer: "Monitor and consider an antihistamine if symptoms worsen"
      },
      {
        id: 28,
        question: "Q28. In a pregnant patient, when is a CT abdomen with contrast recommended?",
        options: [
          "Always safe, no concerns",
          "Only if the benefits outweigh the risks",
          "Never",
          "Only in the third trimester"
        ],
        answer: "Only if the benefits outweigh the risks"
      },
      {
        id: 29,
        question: "Q29. Why should metformin be temporarily stopped after IV contrast administration?",
        options: [
          "It can cause lactic acidosis in patients with renal impairment",
          "It interacts with contrast and causes false imaging results",
          "It enhances the effect of IV contrast",
          "It causes acute pancreatitis"
        ],
        answer: "It can cause lactic acidosis in patients with renal impairment"
      },
      {
        id: 30,
        question: "Q30. What is the best imaging modality for evaluating suspected gallstones if ultrasound is inconclusive?",
        options: [
          "X-ray",
          "CT without contrast",
          "MRI/MRCP",
          "Fluoroscopy"
        ],
        answer: "MRI/MRCP"
      }
    ]
  },
  "304": {
    title: "CT Ennterography Study",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary indication for CT enterography?",
        options: [
          "Liver cirrhosis",
          "Small bowel disorders (e.g., Crohn’s disease)",
          "Pulmonary embolism",
          "Kidney stones"
        ],
        answer: "Small bowel disorders (e.g., Crohn’s disease)"
      },
      {
        id: 2,
        question: "Q2. Which oral contrast is preferred for CT enterography?",
        options: [
          "Iodinated contrast",
          "Low-density (neutral) contrast like VoLumen",
          "Barium sulphate",
          "No oral contrast is needed"
        ],
        answer: "Low-density (neutral) contrast like VoLumen"
      },
      {
        id: 3,
        question: "Q3. Why is neutral oral contrast used in CT enterography?",
        options: [
          "To enhance visualization of the colon",
          "To distend the small bowel for better mucosal evaluation",
          "To improve detection of liver lesions",
          "To reduce radiation exposure"
        ],
        answer: "To distend the small bowel for better mucosal evaluation"
      },
      {
        id: 4,
        question: "Q4. How long should a patient fast before a CT enterography?",
        options: [
          "No fasting required",
          "1 hour",
          "4-6 hours",
          "24 hours"
        ],
        answer: "4-6 hours"
      },
      {
        id: 5,
        question: "Q5. Which condition is best evaluated with CT enterography?",
        options: [
          "Hepatic steatosis",
          "Crohn’s disease",
          "Gallstones",
          "Appendicitis"
        ],
        answer: "Crohn’s disease"
      },
      {
        id: 6,
        question: "Q6. What is the recommended volume of oral contrast for CT enterography?",
        options: [
          "100 mL",
          "1000-1500 mL ",
          "2000-2500 mL",
          "5000 mL"
        ],
        answer: "1000-1500 mL"
      },
      {
        id: 7,
        question: "Q7. How long before scanning should the patient start drinking the oral contrast?",
        options: [
          "45-60 minutes before scanning",
          "5 minutes before scanning",
          "Immediately before scanning",
          "No oral contrast is needed"
        ],
        answer: "45-60 minutes before scanning"
      },
      {
        id: 8,
        question: "Q8. What is the typical IV contrast dose for CT enterography?",
        options: [
          "10 mL",
          "50 mL",
          "100-150 mL",
          "500 mL"
        ],
        answer: "100-150 mL"
      },
      {
        id: 9,
        question: "Q9. What is the recommended IV contrast flow rate for CT enterography?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 10,
        question: "Q10. Which phase is most important for evaluating active inflammation in the small bowel?",
        options: [
          "Enteric phase (50-60 seconds post-injection)",
          "Non-contrast phase",
          "Arterial phase",
          "Delayed phase"
        ],
        answer: "Enteric phase (50-60 seconds post-injection)"
      },
      {
        id: 11,
        question: "Q11. Why is IV contrast necessary in CT enterography?",
        options: [
          "To evaluate lung parenchyma",
          "To differentiate enhancing bowel walls from non-enhancing pathology",
          "To assess for kidney stones",
          "To reduce radiation exposure"
        ],
        answer: "To differentiate enhancing bowel walls from non-enhancing pathology"
      },
      {
        id: 12,
        question: "Q12. What is the standard IV contrast flow rate?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 13,
        question: "Q13. Which contrast phase is best for detecting liver lesions?",
        options: [
          "Non-contrast",
          "Arterial phase",
          "Portal venous phase",
          "Delayed phase"
        ],
        answer: "Portal venous phase"
      },
      {
        id: 14,
        question: "Q14. What is the primary advantage of using dual-phase contrast imaging?",
        options: [
          "Reduced radiation exposure",
          "Improved visualization of vascular and parenchymal structures",
          "Less contrast agent required",
          "Faster scan time"
        ],
        answer: "Improved visualization of vascular and parenchymal structures"
      },
      {
        id: 15,
        question: "Q15. If a patient cannot drink oral contrast, what alternative can be used?",
        options: [
          "Normal saline",
          "IV contrast only",
          "Rectal contrast",
          "No contrast at all"
        ],
        answer: "Rectal contrast"
      },
      {
        id: 16,
        question: "Q16. What finding on a CT abdomen with contrast is most suggestive of appendicitis?",
        options: [
          "Diffuse bowel thickening",
          "Non-enhancing renal cyst",
          "Dilated appendix with surrounding fat stranding",
          "Air-fluid levels in the stomach"
        ],
        answer: "Dilated appendix with surrounding fat stranding"
      },
      {
        id: 17,
        question: "Q17. A \"target sign\" in a contrast-enhanced CT of the abdomen is associated with:",
        options: [
          "Bowel ischemia",
          "Pancreatitis",
          "Gallstones",
          "Kidney cysts"
        ],
        answer: "Bowel ischemia"
      },
      {
        id: 18,
        question: "Q18. A hyperdense lesion in the portal venous phase suggests:",
        options: [
          "Hepatic hemangioma",
          "Simple liver cyst",
          "Liver metastasis",
          "Portal vein thrombosis"
        ],
        answer: "Liver metastasis"
      },
      {
        id: 19,
        question: "Q19. What is the most common finding in acute pancreatitis on contrast-enhanced CT?",
        options: [
          "Pancreatic calcifications",
          "Fatty infiltration",
          "Peripancreatic fluid collection",
          "Hepatomegaly"
        ],
        answer: "Peripancreatic fluid collection"
      },
      {
        id: 20,
        question: "Q20. A contrast-enhancing renal mass on a CT scan is concerning for:",
        options: [
          "Renal cell carcinoma",
          "Simple cyst",
          "Ureteral stone",
          "Pyelonephritis"
        ],
        answer: "Renal cell carcinoma"
      },
      {
        id: 21,
        question: "Q21. What is a potential complication of IV contrast extravasation?",
        options: [
          "Deep vein thrombosis",
          "Soft tissue necrosis",
          "Pneumothorax",
          "Hematuria"
        ],
        answer: "Soft tissue necrosis"
      },
      {
        id: 22,
        question: "Q22. How can motion artifacts be minimized during a CT abdomen scan?",
        options: [
          "Increasing the scan time",
          "Using a higher radiation dose",
          "Coaching the patient on breath-holding",
          "Performing the scan without contrast"
        ],
        answer: "Coaching the patient on breath-holding"
      },
      {
        id: 23,
        question: "Q23. Which artifact may occur due to high-density contrast pooling?",
        options: [
          "Beam-hardening artifact",
          "Ring artifact",
          "Motion artifact",
          "Partial volume artifact"
        ],
        answer: "Beam-hardening artifact"
      },
      {
        id: 24,
        question: "Q24. Which of the following can reduce contrast nephropathy risk?",
        options: [
          "Increasing contrast dose",
          "Hydration with IV fluids",
          "Using MRI instead of CT",
          "Administering epinephrine before contrast"
        ],
        answer: "Hydration with IV fluids"
      },
      {
        id: 25,
        question: "Q25. Why might a non-contrast phase be performed in addition to contrast-enhanced phases?",
        options: [
          "To reduce patient radiation exposure",
          "To evaluate for calcifications or kidney stones",
          "To save scan time",
          "To minimize contrast side effects"
        ],
        answer: "To evaluate for calcifications or kidney stones"
      },
      {
        id: 26,
        question: "Q26. What is the most common side effect of IV contrast administration?",
        options: [
          "Anaphylaxis",
          "Mild nausea or warmth sensation",
          "Seizures",
          "Severe hypotension"
        ],
        answer: "Mild nausea or warmth sensation"
      },
      {
        id: 27,
        question: "Q27. What should be done if a patient experiences mild hives after contrast administration?",
        options: [
          "Monitor and consider an antihistamine if symptoms worsen",
          "Immediately stop the scan and administer epinephrine",
          "Send the patient home without follow-up",
          "Avoid using contrast in future studies"
        ],
        answer: "Monitor and consider an antihistamine if symptoms worsen"
      },
      {
        id: 28,
        question: "Q28. In a pregnant patient, when is a CT abdomen with contrast recommended?",
        options: [
          "Always safe, no concerns",
          "Only if the benefits outweigh the risks",
          "Never",
          "Only in the third trimester"
        ],
        answer: "Only if the benefits outweigh the risks"
      },
      {
        id: 29,
        question: "Q29. Why should metformin be temporarily stopped after IV contrast administration?",
        options: [
          "It can cause lactic acidosis in patients with renal impairment",
          "It interacts with contrast and causes false imaging results",
          "It enhances the effect of IV contrast",
          "It causes acute pancreatitis"
        ],
        answer: "It can cause lactic acidosis in patients with renal impairment"
      },
      {
        id: 30,
        question: "Q30. What is the best imaging modality for evaluating suspected gallstones if ultrasound is inconclusive?",
        options: [
          "X-ray",
          "CT without contrast",
          "MRI/MRCP",
          "Fluoroscopy"
        ],
        answer: "MRI/MRCP"
      }
    ]
  },
  "305": {
    title: "CT KUB Study",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary indication for CT enterography?",
        options: [
          "Liver cirrhosis",
          "Small bowel disorders (e.g., Crohn’s disease)",
          "Pulmonary embolism",
          "Kidney stones"
        ],
        answer: "Small bowel disorders (e.g., Crohn’s disease)"
      },
      {
        id: 2,
        question: "Q2. Which oral contrast is preferred for CT enterography?",
        options: [
          "Iodinated contrast",
          "Low-density (neutral) contrast like VoLumen",
          "Barium sulphate",
          "No oral contrast is needed"
        ],
        answer: "Low-density (neutral) contrast like VoLumen"
      },
      {
        id: 3,
        question: "Q3. Why is neutral oral contrast used in CT enterography?",
        options: [
          "To enhance visualization of the colon",
          "To distend the small bowel for better mucosal evaluation",
          "To improve detection of liver lesions",
          "To reduce radiation exposure"
        ],
        answer: "To distend the small bowel for better mucosal evaluation"
      },
      {
        id: 4,
        question: "Q4. How long should a patient fast before a CT enterography?",
        options: [
          "No fasting required",
          "1 hour",
          "4-6 hours",
          "24 hours"
        ],
        answer: "4-6 hours"
      },
      {
        id: 5,
        question: "Q5. Which condition is best evaluated with CT enterography?",
        options: [
          "Hepatic steatosis",
          "Crohn’s disease",
          "Gallstones",
          "Appendicitis"
        ],
        answer: "Crohn’s disease"
      },
      {
        id: 6,
        question: "Q6. What is the recommended volume of oral contrast for CT enterography?",
        options: [
          "100 mL",
          "1000-1500 mL ",
          "2000-2500 mL",
          "5000 mL"
        ],
        answer: "1000-1500 mL"
      },
      {
        id: 7,
        question: "Q7. How long before scanning should the patient start drinking the oral contrast?",
        options: [
          "45-60 minutes before scanning",
          "5 minutes before scanning",
          "Immediately before scanning",
          "No oral contrast is needed"
        ],
        answer: "45-60 minutes before scanning"
      },
      {
        id: 8,
        question: "Q8. What is the typical IV contrast dose for CT enterography?",
        options: [
          "10 mL",
          "50 mL",
          "100-150 mL",
          "500 mL"
        ],
        answer: "100-150 mL"
      },
      {
        id: 9,
        question: "Q9. What is the recommended IV contrast flow rate for CT enterography?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 10,
        question: "Q10. Which phase is most important for evaluating active inflammation in the small bowel?",
        options: [
          "Enteric phase (50-60 seconds post-injection)",
          "Non-contrast phase",
          "Arterial phase",
          "Delayed phase"
        ],
        answer: "Enteric phase (50-60 seconds post-injection)"
      },
      {
        id: 11,
        question: "Q11. Why is IV contrast necessary in CT enterography?",
        options: [
          "To evaluate lung parenchyma",
          "To differentiate enhancing bowel walls from non-enhancing pathology",
          "To assess for kidney stones",
          "To reduce radiation exposure"
        ],
        answer: "To differentiate enhancing bowel walls from non-enhancing pathology"
      },
      {
        id: 12,
        question: "Q12. What is the standard IV contrast flow rate?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 13,
        question: "Q13. Which contrast phase is best for detecting liver lesions?",
        options: [
          "Non-contrast",
          "Arterial phase",
          "Portal venous phase",
          "Delayed phase"
        ],
        answer: "Portal venous phase"
      },
      {
        id: 14,
        question: "Q14. What is the primary advantage of using dual-phase contrast imaging?",
        options: [
          "Reduced radiation exposure",
          "Improved visualization of vascular and parenchymal structures",
          "Less contrast agent required",
          "Faster scan time"
        ],
        answer: "Improved visualization of vascular and parenchymal structures"
      },
      {
        id: 15,
        question: "Q15. If a patient cannot drink oral contrast, what alternative can be used?",
        options: [
          "Normal saline",
          "IV contrast only",
          "Rectal contrast",
          "No contrast at all"
        ],
        answer: "Rectal contrast"
      },
      {
        id: 16,
        question: "Q16. What finding on a CT abdomen with contrast is most suggestive of appendicitis?",
        options: [
          "Diffuse bowel thickening",
          "Non-enhancing renal cyst",
          "Dilated appendix with surrounding fat stranding",
          "Air-fluid levels in the stomach"
        ],
        answer: "Dilated appendix with surrounding fat stranding"
      },
      {
        id: 17,
        question: "Q17. A \"target sign\" in a contrast-enhanced CT of the abdomen is associated with:",
        options: [
          "Bowel ischemia",
          "Pancreatitis",
          "Gallstones",
          "Kidney cysts"
        ],
        answer: "Bowel ischemia"
      },
      {
        id: 18,
        question: "Q18. A hyperdense lesion in the portal venous phase suggests:",
        options: [
          "Hepatic hemangioma",
          "Simple liver cyst",
          "Liver metastasis",
          "Portal vein thrombosis"
        ],
        answer: "Liver metastasis"
      },
      {
        id: 19,
        question: "Q19. What is the most common finding in acute pancreatitis on contrast-enhanced CT?",
        options: [
          "Pancreatic calcifications",
          "Fatty infiltration",
          "Peripancreatic fluid collection",
          "Hepatomegaly"
        ],
        answer: "Peripancreatic fluid collection"
      },
      {
        id: 20,
        question: "Q20. A contrast-enhancing renal mass on a CT scan is concerning for:",
        options: [
          "Renal cell carcinoma",
          "Simple cyst",
          "Ureteral stone",
          "Pyelonephritis"
        ],
        answer: "Renal cell carcinoma"
      },
      {
        id: 21,
        question: "Q21. What is a potential complication of IV contrast extravasation?",
        options: [
          "Deep vein thrombosis",
          "Soft tissue necrosis",
          "Pneumothorax",
          "Hematuria"
        ],
        answer: "Soft tissue necrosis"
      },
      {
        id: 22,
        question: "Q22. How can motion artifacts be minimized during a CT abdomen scan?",
        options: [
          "Increasing the scan time",
          "Using a higher radiation dose",
          "Coaching the patient on breath-holding",
          "Performing the scan without contrast"
        ],
        answer: "Coaching the patient on breath-holding"
      },
      {
        id: 23,
        question: "Q23. Which artifact may occur due to high-density contrast pooling?",
        options: [
          "Beam-hardening artifact",
          "Ring artifact",
          "Motion artifact",
          "Partial volume artifact"
        ],
        answer: "Beam-hardening artifact"
      },
      {
        id: 24,
        question: "Q24. Which of the following can reduce contrast nephropathy risk?",
        options: [
          "Increasing contrast dose",
          "Hydration with IV fluids",
          "Using MRI instead of CT",
          "Administering epinephrine before contrast"
        ],
        answer: "Hydration with IV fluids"
      },
      {
        id: 25,
        question: "Q25. Why might a non-contrast phase be performed in addition to contrast-enhanced phases?",
        options: [
          "To reduce patient radiation exposure",
          "To evaluate for calcifications or kidney stones",
          "To save scan time",
          "To minimize contrast side effects"
        ],
        answer: "To evaluate for calcifications or kidney stones"
      },
      {
        id: 26,
        question: "Q26. What is the most common side effect of IV contrast administration?",
        options: [
          "Anaphylaxis",
          "Mild nausea or warmth sensation",
          "Seizures",
          "Severe hypotension"
        ],
        answer: "Mild nausea or warmth sensation"
      },
      {
        id: 27,
        question: "Q27. What should be done if a patient experiences mild hives after contrast administration?",
        options: [
          "Monitor and consider an antihistamine if symptoms worsen",
          "Immediately stop the scan and administer epinephrine",
          "Send the patient home without follow-up",
          "Avoid using contrast in future studies"
        ],
        answer: "Monitor and consider an antihistamine if symptoms worsen"
      },
      {
        id: 28,
        question: "Q28. In a pregnant patient, when is a CT abdomen with contrast recommended?",
        options: [
          "Always safe, no concerns",
          "Only if the benefits outweigh the risks",
          "Never",
          "Only in the third trimester"
        ],
        answer: "Only if the benefits outweigh the risks"
      },
      {
        id: 29,
        question: "Q29. Why should metformin be temporarily stopped after IV contrast administration?",
        options: [
          "It can cause lactic acidosis in patients with renal impairment",
          "It interacts with contrast and causes false imaging results",
          "It enhances the effect of IV contrast",
          "It causes acute pancreatitis"
        ],
        answer: "It can cause lactic acidosis in patients with renal impairment"
      },
      {
        id: 30,
        question: "Q30. What is the best imaging modality for evaluating suspected gallstones if ultrasound is inconclusive?",
        options: [
          "X-ray",
          "CT without contrast",
          "MRI/MRCP",
          "Fluoroscopy"
        ],
        answer: "MRI/MRCP"
      }
    ]
  },
  "306": {
    title: "CT Urography Study",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the primary indication for CT enterography?",
        options: [
          "Liver cirrhosis",
          "Small bowel disorders (e.g., Crohn’s disease)",
          "Pulmonary embolism",
          "Kidney stones"
        ],
        answer: "Small bowel disorders (e.g., Crohn’s disease)"
      },
      {
        id: 2,
        question: "Q2. Which oral contrast is preferred for CT enterography?",
        options: [
          "Iodinated contrast",
          "Low-density (neutral) contrast like VoLumen",
          "Barium sulphate",
          "No oral contrast is needed"
        ],
        answer: "Low-density (neutral) contrast like VoLumen"
      },
      {
        id: 3,
        question: "Q3. Why is neutral oral contrast used in CT enterography?",
        options: [
          "To enhance visualization of the colon",
          "To distend the small bowel for better mucosal evaluation",
          "To improve detection of liver lesions",
          "To reduce radiation exposure"
        ],
        answer: "To distend the small bowel for better mucosal evaluation"
      },
      {
        id: 4,
        question: "Q4. How long should a patient fast before a CT enterography?",
        options: [
          "No fasting required",
          "1 hour",
          "4-6 hours",
          "24 hours"
        ],
        answer: "4-6 hours"
      },
      {
        id: 5,
        question: "Q5. Which condition is best evaluated with CT enterography?",
        options: [
          "Hepatic steatosis",
          "Crohn’s disease",
          "Gallstones",
          "Appendicitis"
        ],
        answer: "Crohn’s disease"
      },
      {
        id: 6,
        question: "Q6. What is the recommended volume of oral contrast for CT enterography?",
        options: [
          "100 mL",
          "1000-1500 mL ",
          "2000-2500 mL",
          "5000 mL"
        ],
        answer: "1000-1500 mL"
      },
      {
        id: 7,
        question: "Q7. How long before scanning should the patient start drinking the oral contrast?",
        options: [
          "45-60 minutes before scanning",
          "5 minutes before scanning",
          "Immediately before scanning",
          "No oral contrast is needed"
        ],
        answer: "45-60 minutes before scanning"
      },
      {
        id: 8,
        question: "Q8. What is the typical IV contrast dose for CT enterography?",
        options: [
          "10 mL",
          "50 mL",
          "100-150 mL",
          "500 mL"
        ],
        answer: "100-150 mL"
      },
      {
        id: 9,
        question: "Q9. What is the recommended IV contrast flow rate for CT enterography?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 10,
        question: "Q10. Which phase is most important for evaluating active inflammation in the small bowel?",
        options: [
          "Enteric phase (50-60 seconds post-injection)",
          "Non-contrast phase",
          "Arterial phase",
          "Delayed phase"
        ],
        answer: "Enteric phase (50-60 seconds post-injection)"
      },
      {
        id: 11,
        question: "Q11. Why is IV contrast necessary in CT enterography?",
        options: [
          "To evaluate lung parenchyma",
          "To differentiate enhancing bowel walls from non-enhancing pathology",
          "To assess for kidney stones",
          "To reduce radiation exposure"
        ],
        answer: "To differentiate enhancing bowel walls from non-enhancing pathology"
      },
      {
        id: 12,
        question: "Q12. What is the standard IV contrast flow rate?",
        options: [
          "0.5 mL/sec",
          "1.0 mL/sec",
          "3-5 mL/sec",
          "10 mL/sec"
        ],
        answer: "3-5 mL/sec"
      },
      {
        id: 13,
        question: "Q13. Which contrast phase is best for detecting liver lesions?",
        options: [
          "Non-contrast",
          "Arterial phase",
          "Portal venous phase",
          "Delayed phase"
        ],
        answer: "Portal venous phase"
      },
      {
        id: 14,
        question: "Q14. What is the primary advantage of using dual-phase contrast imaging?",
        options: [
          "Reduced radiation exposure",
          "Improved visualization of vascular and parenchymal structures",
          "Less contrast agent required",
          "Faster scan time"
        ],
        answer: "Improved visualization of vascular and parenchymal structures"
      },
      {
        id: 15,
        question: "Q15. If a patient cannot drink oral contrast, what alternative can be used?",
        options: [
          "Normal saline",
          "IV contrast only",
          "Rectal contrast",
          "No contrast at all"
        ],
        answer: "Rectal contrast"
      },
      {
        id: 16,
        question: "Q16. What finding on a CT abdomen with contrast is most suggestive of appendicitis?",
        options: [
          "Diffuse bowel thickening",
          "Non-enhancing renal cyst",
          "Dilated appendix with surrounding fat stranding",
          "Air-fluid levels in the stomach"
        ],
        answer: "Dilated appendix with surrounding fat stranding"
      },
      {
        id: 17,
        question: "Q17. A \"target sign\" in a contrast-enhanced CT of the abdomen is associated with:",
        options: [
          "Bowel ischemia",
          "Pancreatitis",
          "Gallstones",
          "Kidney cysts"
        ],
        answer: "Bowel ischemia"
      },
      {
        id: 18,
        question: "Q18. A hyperdense lesion in the portal venous phase suggests:",
        options: [
          "Hepatic hemangioma",
          "Simple liver cyst",
          "Liver metastasis",
          "Portal vein thrombosis"
        ],
        answer: "Liver metastasis"
      },
      {
        id: 19,
        question: "Q19. What is the most common finding in acute pancreatitis on contrast-enhanced CT?",
        options: [
          "Pancreatic calcifications",
          "Fatty infiltration",
          "Peripancreatic fluid collection",
          "Hepatomegaly"
        ],
        answer: "Peripancreatic fluid collection"
      },
      {
        id: 20,
        question: "Q20. A contrast-enhancing renal mass on a CT scan is concerning for:",
        options: [
          "Renal cell carcinoma",
          "Simple cyst",
          "Ureteral stone",
          "Pyelonephritis"
        ],
        answer: "Renal cell carcinoma"
      },
      {
        id: 21,
        question: "Q21. What is a potential complication of IV contrast extravasation?",
        options: [
          "Deep vein thrombosis",
          "Soft tissue necrosis",
          "Pneumothorax",
          "Hematuria"
        ],
        answer: "Soft tissue necrosis"
      },
      {
        id: 22,
        question: "Q22. How can motion artifacts be minimized during a CT abdomen scan?",
        options: [
          "Increasing the scan time",
          "Using a higher radiation dose",
          "Coaching the patient on breath-holding",
          "Performing the scan without contrast"
        ],
        answer: "Coaching the patient on breath-holding"
      },
      {
        id: 23,
        question: "Q23. Which artifact may occur due to high-density contrast pooling?",
        options: [
          "Beam-hardening artifact",
          "Ring artifact",
          "Motion artifact",
          "Partial volume artifact"
        ],
        answer: "Beam-hardening artifact"
      },
      {
        id: 24,
        question: "Q24. Which of the following can reduce contrast nephropathy risk?",
        options: [
          "Increasing contrast dose",
          "Hydration with IV fluids",
          "Using MRI instead of CT",
          "Administering epinephrine before contrast"
        ],
        answer: "Hydration with IV fluids"
      },
      {
        id: 25,
        question: "Q25. Why might a non-contrast phase be performed in addition to contrast-enhanced phases?",
        options: [
          "To reduce patient radiation exposure",
          "To evaluate for calcifications or kidney stones",
          "To save scan time",
          "To minimize contrast side effects"
        ],
        answer: "To evaluate for calcifications or kidney stones"
      },
      {
        id: 26,
        question: "Q26. What is the most common side effect of IV contrast administration?",
        options: [
          "Anaphylaxis",
          "Mild nausea or warmth sensation",
          "Seizures",
          "Severe hypotension"
        ],
        answer: "Mild nausea or warmth sensation"
      },
      {
        id: 27,
        question: "Q27. What should be done if a patient experiences mild hives after contrast administration?",
        options: [
          "Monitor and consider an antihistamine if symptoms worsen",
          "Immediately stop the scan and administer epinephrine",
          "Send the patient home without follow-up",
          "Avoid using contrast in future studies"
        ],
        answer: "Monitor and consider an antihistamine if symptoms worsen"
      },
      {
        id: 28,
        question: "Q28. In a pregnant patient, when is a CT abdomen with contrast recommended?",
        options: [
          "Always safe, no concerns",
          "Only if the benefits outweigh the risks",
          "Never",
          "Only in the third trimester"
        ],
        answer: "Only if the benefits outweigh the risks"
      },
      {
        id: 29,
        question: "Q29. Why should metformin be temporarily stopped after IV contrast administration?",
        options: [
          "It can cause lactic acidosis in patients with renal impairment",
          "It interacts with contrast and causes false imaging results",
          "It enhances the effect of IV contrast",
          "It causes acute pancreatitis"
        ],
        answer: "It can cause lactic acidosis in patients with renal impairment"
      },
      {
        id: 30,
        question: "Q30. What is the best imaging modality for evaluating suspected gallstones if ultrasound is inconclusive?",
        options: [
          "X-ray",
          "CT without contrast",
          "MRI/MRCP",
          "Fluoroscopy"
        ],
        answer: "MRI/MRCP"
      }
    ]
  },
  "401": {
    title: "CT Pelvis Plain",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the most common clinical indication for a CT Bony Pelvis?​",
        options: [
          "Appendicitis​",
          "Kidney stone​",
          "Pelvic trauma",
          "Hernia​"
        ],
        answer: "Pelvic trauma"
      },
      {
        id: 2,
        question: "Q2. The bony pelvis includes all the following except:​",
        options: [
          "Ilium​",
          "Sacrum​",
          "Femoral condyles",
          "Pubis"
        ],
        answer: "Femoral condyles"
      },
      {
        id: 3,
        question: "Q3. CT is the imaging modality of choice for:",
        options: [
          "Soft tissue abscess",
          "Complex pelvic fractures",
          "muscle strain",
          "Herniated disc"
        ],
        answer: "Complex pelvic fractures"
      },
      {
        id: 4,
        question: "Q4. What is the typical scan range for a CT bony pelvis?​",
        options: [
          "Thoracic inlet to diaphragm​",
          "From iliac crests to ischial tuberosities",
          "Lower limbs only",
          "Cervical spine"
        ],
        answer: "lliac crests to ischial tuberosities"
      },
      {
        id: 5,
        question: "Q5. In trauma, CT bony pelvis is essential for identifying:​",
        options: [
          "Muscle tears​",
          "Sacral fractures",
          "Appendicitis​",
          "Bladder stones"
        ],
        answer: "Sacral fractures"
      },
      {
        id: 6,
        question: "Q6. When is contrast typically used in a bony pelvis CT?​",
        options: [
          "Bone tumor",
          "Pelvic fracture​ ",
          "Suspected infection or tumor extension",
          "Osteoarthritis"
        ],
        answer: "Suspected infection or tumor extension"
      },
      {
        id: 7,
        question: "Q7.  What is a common cause of pelvic ring disruption?​",
        options: [
          "High-energy trauma (e.g., MVA) ​",
          "Stroke​",
          "Appendicitis​",
          "Osteroarthritis"
        ],
        answer: "High-energy trauma (e.g., MVA)"
      },
      {
        id: 8,
        question: "Q8. The acetabulum is part of the:​",
        options: [
          "Sacrum​",
          "Ischium only",
          "Hip joint(formed by ilium, ischium, and pubis)",
          "Femur"
        ],
        answer: "Hip joint(formed by ilium, ischium, and pubis)"
      },
      {
        id: 9,
        question: "Q9. A vertical shear injury of the pelvis typically results in:​",
        options: [
          "Anterior subluxation",
          "Asymmetric elevation of a hemipelvis ",
          "Herniation​",
          "Bladder rupture"
        ],
        answer: "Asymmetric elevation of a hemipelvis"
      },
      {
        id: 10,
        question: "Q10. What window setting is best for bone evaluation in CT?​",
        options: [
          "Brain",
          "Soft tissue",
          "Lung",
          "Bone window",
        ],
        answer: "Bone window"
      },
      {
        id: 11,
        question: "Q11. Which of these fractures is commonly associated with pelvic trauma?​",
        options: [
          "Jefferson fracture​",
          "Open book fracture",
          "Hangman's fracture",
          "Le Fort fracture"
        ],
        answer: "Open book fracture"
      },
      {
        id: 12,
        question: "Q12. Which pelvic structure articulates with the femur?​",
        options: [
          "Acetabulum",
          "lliac crest",
          "Sacral ala",
          "Public symphysis"
        ],
        answer: "Acetabulum"
      },
      {
        id: 13,
        question: "Q13. Which imaging plane is most useful for assessing pubic rami fractures?​",
        options: [
          "Axial only",
          "Coronal reformals",
          "Oblique",
          "Lateral"
        ],
        answer: "Coronal reformals"
      },
      {
        id: 14,
        question: "Q14. The sacroiliac joint is located between:",
        options: [
          "llium and acetabulum",
          "llium and sacrum",
          "Pubis and ischium",
          "Femur and tibia"
        ],
        answer: "llium and sacrum"
      },
      {
        id: 15,
        question: "Q15. In trauma, a widened pubic symphysis suggests:​",
        options: [
          "Osteitis",
          "Pelvic ring instability",
          "Scolliosis",
          "Normal variant"
        ],
        answer: "Pelvic ring instability"
      },
      {
        id: 16,
        question: "Q16. What CT finding indicates pelvic hemorrhage?​",
        options: [
          "Soft tissue swelling​",
          "Contrast extravasation in arterial pelvis",
          "Fat stranding",
          "Gas in joint space"
        ],
        answer: "Contrast extravasation in arterial pelvis"
      },
      {
        id: 17,
        question: "Q17. Which bone forms the posterior portion of the pelvic ring?​",
        options: [
          "Pubis",
          "llium",
          "Sacrum",
          "Ischium"
        ],
        answer: "Sacrum"
      },
      {
        id: 18,
        question: "Q18. What is a benefit of CT over plain X-ray in pelvis imaging?",
        options: [
          "Cheaper",
          "3D fracture mapping",
          "Lower radiation",
          "Better soft tissue contrast than MRI"
        ],
        answer: "3D fracture mapping"
      },
      {
        id: 19,
        question: "Q19. What is the primary concern in unstable pelvic fractures?​",
        options: [
          "Nerve pain",
          "Soft tissue swelling",
          "Hemorrhage and organ injury",
          "Muscle weakness"
        ],
        answer: "Hemorrhage and organ injury"
      },
      {
        id: 20,
        question: "Q20. Which of these bones is not part of the pelvic girdle?​",
        options: [
          "llium",
          "Femur",
          "Ischium",
          "Pubis"
        ],
        answer: "Femur"
      },
      {
        id: 21,
        question: "Q21. The pelvic brim divides the pelvis into:",
        options: [
          "Right and left halves",
          "Posterior and anterior portions",
          "True and false pelvis",
          "Outer and inner bones"
        ],
        answer: "True and false pelvis"
      },
      {
        id: 22,
        question: "Q22. Which finding on CT suggests pelvic osteomyelitis?​",
        options: [
          "Joint dislocation",
          "Lytic bone lesions with sift tissue changes",
          "Dense sclerosis",
          "Pubic diastasis"
        ],
        answer: "Lytic bone lesions with sift tissue changes"
      },
      {
        id: 23,
        question: "Q23. What is the role of contrast in CT pelvis for tumors?​",
        options: [
          "Outlines bones",
          "Highlight bones",
          "Enhances vascular supply and soft tissue planes",
          "Reduce noise"
        ],
        answer: "Enhances vascular supply and soft tissue planes"
      },
      {
        id: 24,
        question: "Q24. Which fracture type involves the sacroiliac joint and iliac wing?​",
        options: [
          "Le Fort",
          "Hungman's",
          "Crescent fracture",
          "Boxer's fracture"
        ],
        answer: "Crescent fracture"
      },
      {
        id: 25,
        question: "Q25. What is a contraindication for IV contrast in CT pelvis?​?​",
        options: [
          "fracture",
          "Haematuria",
          "Renal failure",
          "Pelvic pain"
        ],
        answer: "Renal failure"
      },
      {
        id: 26,
        question: "Q26. What technique can reduce dose in pelvic CT?​​ ",
        options: [
          "increase kVp",
          "Manual mA setting",
          "Automatic exposure control (AEC)",
          "Use contrast only"
        ],
        answer: "Automatic exposure control (AEC)"
      },
      {
        id: 27,
        question: "Q27. What does metal artifact reduction help with?​",
        options: [
          "Prosthesis or fixation hardware clarity ​",
          "Increasing dose",
          "Changing Slice thickness",
          "Enhancing fat planes"
        ],
        answer: "Prosthesis or fixation hardware clarity"
      },
      {
        id: 28,
        question: "Q28. Sacral insufficiency fractures often occur in:​",
        options: [
          "Children",
          "Athletes",
          "Elderly with osteoporosis",
          "Soldiers"
        ],
        answer: "Elderly with osteoporosis"
      },
      {
        id: 29,
        question: "Q29. Which part of the pelvis is involved in a straddle fracture?​",
        options: [
          "lliac wings",
          "Acetabulum",
          "Both superior and inferior pubic rami bilateral",
          "Sacrum only"
        ],
        answer: "Both superior and inferior pubic rami bilateral"
      },
      {
        id: 30,
        question: "Q30. What is the main use of 3D reconstruction in pelvic CT?​",
        options: [
          "Showing muscle tears",
          "Surgical planning and fracture visualization ",
          "Comparing organs",
          "Measuring fat"
        ],
        answer: "Surgical planning and fracture visualization "
      }
    ]
  },
  "402": {
    title: "CT Pelvis Contrast",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the most common clinical indication for a CT Bony Pelvis?​",
        options: [
          "Appendicitis​",
          "Kidney stone​",
          "Pelvic trauma",
          "Hernia​"
        ],
        answer: "Pelvic trauma"
      },
      {
        id: 2,
        question: "Q2. The bony pelvis includes all the following except:​",
        options: [
          "Ilium​",
          "Sacrum​",
          "Femoral condyles",
          "Pubis"
        ],
        answer: "Femoral condyles"
      },
      {
        id: 3,
        question: "Q3. CT is the imaging modality of choice for:",
        options: [
          "Soft tissue abscess",
          "Complex pelvic fractures",
          "muscle strain",
          "Herniated disc"
        ],
        answer: "Complex pelvic fractures"
      },
      {
        id: 4,
        question: "Q4. What is the typical scan range for a CT bony pelvis?​",
        options: [
          "Thoracic inlet to diaphragm​",
          "From iliac crests to ischial tuberosities",
          "Lower limbs only",
          "Cervical spine"
        ],
        answer: "lliac crests to ischial tuberosities"
      },
      {
        id: 5,
        question: "Q5. In trauma, CT bony pelvis is essential for identifying:​",
        options: [
          "Muscle tears​",
          "Sacral fractures",
          "Appendicitis​",
          "Bladder stones"
        ],
        answer: "Sacral fractures"
      },
      {
        id: 6,
        question: "Q6. When is contrast typically used in a bony pelvis CT?​",
        options: [
          "Bone tumor",
          "Pelvic fracture​ ",
          "Suspected infection or tumor extension",
          "Osteoarthritis"
        ],
        answer: "Suspected infection or tumor extension"
      },
      {
        id: 7,
        question: "Q7.  What is a common cause of pelvic ring disruption?​",
        options: [
          "High-energy trauma (e.g., MVA) ​",
          "Stroke​",
          "Appendicitis​",
          "Osteroarthritis"
        ],
        answer: "High-energy trauma (e.g., MVA)"
      },
      {
        id: 8,
        question: "Q8. The acetabulum is part of the:​",
        options: [
          "Sacrum​",
          "Ischium only",
          "Hip joint(formed by ilium, ischium, and pubis)",
          "Femur"
        ],
        answer: "Hip joint(formed by ilium, ischium, and pubis)"
      },
      {
        id: 9,
        question: "Q9. A vertical shear injury of the pelvis typically results in:​",
        options: [
          "Anterior subluxation",
          "Asymmetric elevation of a hemipelvis ",
          "Herniation​",
          "Bladder rupture"
        ],
        answer: "Asymmetric elevation of a hemipelvis"
      },
      {
        id: 10,
        question: "Q10. What window setting is best for bone evaluation in CT?​",
        options: [
          "Brain",
          "Soft tissue",
          "Lung",
          "Bone window",
        ],
        answer: "Bone window"
      },
      {
        id: 11,
        question: "Q11. Which of these fractures is commonly associated with pelvic trauma?​",
        options: [
          "Jefferson fracture​",
          "Open book fracture",
          "Hangman's fracture",
          "Le Fort fracture"
        ],
        answer: "Open book fracture"
      },
      {
        id: 12,
        question: "Q12. Which pelvic structure articulates with the femur?​",
        options: [
          "Acetabulum",
          "lliac crest",
          "Sacral ala",
          "Public symphysis"
        ],
        answer: "Acetabulum"
      },
      {
        id: 13,
        question: "Q13. Which imaging plane is most useful for assessing pubic rami fractures?​",
        options: [
          "Axial only",
          "Coronal reformals",
          "Oblique",
          "Lateral"
        ],
        answer: "Coronal reformals"
      },
      {
        id: 14,
        question: "Q14. The sacroiliac joint is located between:",
        options: [
          "llium and acetabulum",
          "llium and sacrum",
          "Pubis and ischium",
          "Femur and tibia"
        ],
        answer: "llium and sacrum"
      },
      {
        id: 15,
        question: "Q15. In trauma, a widened pubic symphysis suggests:​",
        options: [
          "Osteitis",
          "Pelvic ring instability",
          "Scolliosis",
          "Normal variant"
        ],
        answer: "Pelvic ring instability"
      },
      {
        id: 16,
        question: "Q16. What CT finding indicates pelvic hemorrhage?​",
        options: [
          "Soft tissue swelling​",
          "Contrast extravasation in arterial pelvis",
          "Fat stranding",
          "Gas in joint space"
        ],
        answer: "Contrast extravasation in arterial pelvis"
      },
      {
        id: 17,
        question: "Q17. Which bone forms the posterior portion of the pelvic ring?​",
        options: [
          "Pubis",
          "llium",
          "Sacrum",
          "Ischium"
        ],
        answer: "Sacrum"
      },
      {
        id: 18,
        question: "Q18. What is a benefit of CT over plain X-ray in pelvis imaging?",
        options: [
          "Cheaper",
          "3D fracture mapping",
          "Lower radiation",
          "Better soft tissue contrast than MRI"
        ],
        answer: "3D fracture mapping"
      },
      {
        id: 19,
        question: "Q19. What is the primary concern in unstable pelvic fractures?​",
        options: [
          "Nerve pain",
          "Soft tissue swelling",
          "Hemorrhage and organ injury",
          "Muscle weakness"
        ],
        answer: "Hemorrhage and organ injury"
      },
      {
        id: 20,
        question: "Q20. Which of these bones is not part of the pelvic girdle?​",
        options: [
          "llium",
          "Femur",
          "Ischium",
          "Pubis"
        ],
        answer: "Femur"
      },
      {
        id: 21,
        question: "Q21. The pelvic brim divides the pelvis into:",
        options: [
          "Right and left halves",
          "Posterior and anterior portions",
          "True and false pelvis",
          "Outer and inner bones"
        ],
        answer: "True and false pelvis"
      },
      {
        id: 22,
        question: "Q22. Which finding on CT suggests pelvic osteomyelitis?​",
        options: [
          "Joint dislocation",
          "Lytic bone lesions with sift tissue changes",
          "Dense sclerosis",
          "Pubic diastasis"
        ],
        answer: "Lytic bone lesions with sift tissue changes"
      },
      {
        id: 23,
        question: "Q23. What is the role of contrast in CT pelvis for tumors?​",
        options: [
          "Outlines bones",
          "Highlight bones",
          "Enhances vascular supply and soft tissue planes",
          "Reduce noise"
        ],
        answer: "Enhances vascular supply and soft tissue planes"
      },
      {
        id: 24,
        question: "Q24. Which fracture type involves the sacroiliac joint and iliac wing?​",
        options: [
          "Le Fort",
          "Hungman's",
          "Crescent fracture",
          "Boxer's fracture"
        ],
        answer: "Crescent fracture"
      },
      {
        id: 25,
        question: "Q25. What is a contraindication for IV contrast in CT pelvis?​?​",
        options: [
          "fracture",
          "Haematuria",
          "Renal failure",
          "Pelvic pain"
        ],
        answer: "Renal failure"
      },
      {
        id: 26,
        question: "Q26. What technique can reduce dose in pelvic CT?​​ ",
        options: [
          "increase kVp",
          "Manual mA setting",
          "Automatic exposure control (AEC)",
          "Use contrast only"
        ],
        answer: "Automatic exposure control (AEC)"
      },
      {
        id: 27,
        question: "Q27. What does metal artifact reduction help with?​",
        options: [
          "Prosthesis or fixation hardware clarity ​",
          "Increasing dose",
          "Changing Slice thickness",
          "Enhancing fat planes"
        ],
        answer: "Prosthesis or fixation hardware clarity"
      },
      {
        id: 28,
        question: "Q28. Sacral insufficiency fractures often occur in:​",
        options: [
          "Children",
          "Athletes",
          "Elderly with osteoporosis",
          "Soldiers"
        ],
        answer: "Elderly with osteoporosis"
      },
      {
        id: 29,
        question: "Q29. Which part of the pelvis is involved in a straddle fracture?​",
        options: [
          "lliac wings",
          "Acetabulum",
          "Both superior and inferior pubic rami bilateral",
          "Sacrum only"
        ],
        answer: "Both superior and inferior pubic rami bilateral"
      },
      {
        id: 30,
        question: "Q30. What is the main use of 3D reconstruction in pelvic CT?​",
        options: [
          "Showing muscle tears",
          "Surgical planning and fracture visualization ",
          "Comparing organs",
          "Measuring fat"
        ],
        answer: "Surgical planning and fracture visualization "
      }
    ]
  },
  "403": {
    title: "CT Hip Protocol",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the most common clinical indication for a CT Bony Pelvis?​",
        options: [
          "Appendicitis​",
          "Kidney stone​",
          "Pelvic trauma",
          "Hernia​"
        ],
        answer: "Pelvic trauma"
      },
      {
        id: 2,
        question: "Q2. The bony pelvis includes all the following except:​",
        options: [
          "Ilium​",
          "Sacrum​",
          "Femoral condyles",
          "Pubis"
        ],
        answer: "Femoral condyles"
      },
      {
        id: 3,
        question: "Q3. CT is the imaging modality of choice for:",
        options: [
          "Soft tissue abscess",
          "Complex pelvic fractures",
          "muscle strain",
          "Herniated disc"
        ],
        answer: "Complex pelvic fractures"
      },
      {
        id: 4,
        question: "Q4. What is the typical scan range for a CT bony pelvis?​",
        options: [
          "Thoracic inlet to diaphragm​",
          "From iliac crests to ischial tuberosities",
          "Lower limbs only",
          "Cervical spine"
        ],
        answer: "lliac crests to ischial tuberosities"
      },
      {
        id: 5,
        question: "Q5. In trauma, CT bony pelvis is essential for identifying:​",
        options: [
          "Muscle tears​",
          "Sacral fractures",
          "Appendicitis​",
          "Bladder stones"
        ],
        answer: "Sacral fractures"
      },
      {
        id: 6,
        question: "Q6. When is contrast typically used in a bony pelvis CT?​",
        options: [
          "Bone tumor",
          "Pelvic fracture​ ",
          "Suspected infection or tumor extension",
          "Osteoarthritis"
        ],
        answer: "Suspected infection or tumor extension"
      },
      {
        id: 7,
        question: "Q7.  What is a common cause of pelvic ring disruption?​",
        options: [
          "High-energy trauma (e.g., MVA) ​",
          "Stroke​",
          "Appendicitis​",
          "Osteroarthritis"
        ],
        answer: "High-energy trauma (e.g., MVA)"
      },
      {
        id: 8,
        question: "Q8. The acetabulum is part of the:​",
        options: [
          "Sacrum​",
          "Ischium only",
          "Hip joint(formed by ilium, ischium, and pubis)",
          "Femur"
        ],
        answer: "Hip joint(formed by ilium, ischium, and pubis)"
      },
      {
        id: 9,
        question: "Q9. A vertical shear injury of the pelvis typically results in:​",
        options: [
          "Anterior subluxation",
          "Asymmetric elevation of a hemipelvis ",
          "Herniation​",
          "Bladder rupture"
        ],
        answer: "Asymmetric elevation of a hemipelvis"
      },
      {
        id: 10,
        question: "Q10. What window setting is best for bone evaluation in CT?​",
        options: [
          "Brain",
          "Soft tissue",
          "Lung",
          "Bone window",
        ],
        answer: "Bone window"
      },
      {
        id: 11,
        question: "Q11. Which of these fractures is commonly associated with pelvic trauma?​",
        options: [
          "Jefferson fracture​",
          "Open book fracture",
          "Hangman's fracture",
          "Le Fort fracture"
        ],
        answer: "Open book fracture"
      },
      {
        id: 12,
        question: "Q12. Which pelvic structure articulates with the femur?​",
        options: [
          "Acetabulum",
          "lliac crest",
          "Sacral ala",
          "Public symphysis"
        ],
        answer: "Acetabulum"
      },
      {
        id: 13,
        question: "Q13. Which imaging plane is most useful for assessing pubic rami fractures?​",
        options: [
          "Axial only",
          "Coronal reformals",
          "Oblique",
          "Lateral"
        ],
        answer: "Coronal reformals"
      },
      {
        id: 14,
        question: "Q14. The sacroiliac joint is located between:",
        options: [
          "llium and acetabulum",
          "llium and sacrum",
          "Pubis and ischium",
          "Femur and tibia"
        ],
        answer: "llium and sacrum"
      },
      {
        id: 15,
        question: "Q15. In trauma, a widened pubic symphysis suggests:​",
        options: [
          "Osteitis",
          "Pelvic ring instability",
          "Scolliosis",
          "Normal variant"
        ],
        answer: "Pelvic ring instability"
      },
      {
        id: 16,
        question: "Q16. What CT finding indicates pelvic hemorrhage?​",
        options: [
          "Soft tissue swelling​",
          "Contrast extravasation in arterial pelvis",
          "Fat stranding",
          "Gas in joint space"
        ],
        answer: "Contrast extravasation in arterial pelvis"
      },
      {
        id: 17,
        question: "Q17. Which bone forms the posterior portion of the pelvic ring?​",
        options: [
          "Pubis",
          "llium",
          "Sacrum",
          "Ischium"
        ],
        answer: "Sacrum"
      },
      {
        id: 18,
        question: "Q18. What is a benefit of CT over plain X-ray in pelvis imaging?",
        options: [
          "Cheaper",
          "3D fracture mapping",
          "Lower radiation",
          "Better soft tissue contrast than MRI"
        ],
        answer: "3D fracture mapping"
      },
      {
        id: 19,
        question: "Q19. What is the primary concern in unstable pelvic fractures?​",
        options: [
          "Nerve pain",
          "Soft tissue swelling",
          "Hemorrhage and organ injury",
          "Muscle weakness"
        ],
        answer: "Hemorrhage and organ injury"
      },
      {
        id: 20,
        question: "Q20. Which of these bones is not part of the pelvic girdle?​",
        options: [
          "llium",
          "Femur",
          "Ischium",
          "Pubis"
        ],
        answer: "Femur"
      },
      {
        id: 21,
        question: "Q21. The pelvic brim divides the pelvis into:",
        options: [
          "Right and left halves",
          "Posterior and anterior portions",
          "True and false pelvis",
          "Outer and inner bones"
        ],
        answer: "True and false pelvis"
      },
      {
        id: 22,
        question: "Q22. Which finding on CT suggests pelvic osteomyelitis?​",
        options: [
          "Joint dislocation",
          "Lytic bone lesions with sift tissue changes",
          "Dense sclerosis",
          "Pubic diastasis"
        ],
        answer: "Lytic bone lesions with sift tissue changes"
      },
      {
        id: 23,
        question: "Q23. What is the role of contrast in CT pelvis for tumors?​",
        options: [
          "Outlines bones",
          "Highlight bones",
          "Enhances vascular supply and soft tissue planes",
          "Reduce noise"
        ],
        answer: "Enhances vascular supply and soft tissue planes"
      },
      {
        id: 24,
        question: "Q24. Which fracture type involves the sacroiliac joint and iliac wing?​",
        options: [
          "Le Fort",
          "Hungman's",
          "Crescent fracture",
          "Boxer's fracture"
        ],
        answer: "Crescent fracture"
      },
      {
        id: 25,
        question: "Q25. What is a contraindication for IV contrast in CT pelvis?​?​",
        options: [
          "fracture",
          "Haematuria",
          "Renal failure",
          "Pelvic pain"
        ],
        answer: "Renal failure"
      },
      {
        id: 26,
        question: "Q26. What technique can reduce dose in pelvic CT?​​ ",
        options: [
          "increase kVp",
          "Manual mA setting",
          "Automatic exposure control (AEC)",
          "Use contrast only"
        ],
        answer: "Automatic exposure control (AEC)"
      },
      {
        id: 27,
        question: "Q27. What does metal artifact reduction help with?​",
        options: [
          "Prosthesis or fixation hardware clarity ​",
          "Increasing dose",
          "Changing Slice thickness",
          "Enhancing fat planes"
        ],
        answer: "Prosthesis or fixation hardware clarity"
      },
      {
        id: 28,
        question: "Q28. Sacral insufficiency fractures often occur in:​",
        options: [
          "Children",
          "Athletes",
          "Elderly with osteoporosis",
          "Soldiers"
        ],
        answer: "Elderly with osteoporosis"
      },
      {
        id: 29,
        question: "Q29. Which part of the pelvis is involved in a straddle fracture?​",
        options: [
          "lliac wings",
          "Acetabulum",
          "Both superior and inferior pubic rami bilateral",
          "Sacrum only"
        ],
        answer: "Both superior and inferior pubic rami bilateral"
      },
      {
        id: 30,
        question: "Q30. What is the main use of 3D reconstruction in pelvic CT?​",
        options: [
          "Showing muscle tears",
          "Surgical planning and fracture visualization ",
          "Comparing organs",
          "Measuring fat"
        ],
        answer: "Surgical planning and fracture visualization "
      }
    ]
  },
  "404": {
    title: "CT Bony Pelvic Study",
    description: "Test your knowledge about contrast-enhanced CT of the abdomen.",
    questions: [
      {
        id: 1,
        question: "Q1. What is the most common clinical indication for a CT Bony Pelvis?​",
        options: [
          "Appendicitis​",
          "Kidney stone​",
          "Pelvic trauma",
          "Hernia​"
        ],
        answer: "Pelvic trauma"
      },
      {
        id: 2,
        question: "Q2. The bony pelvis includes all the following except:​",
        options: [
          "Ilium​",
          "Sacrum​",
          "Femoral condyles",
          "Pubis"
        ],
        answer: "Femoral condyles"
      },
      {
        id: 3,
        question: "Q3. CT is the imaging modality of choice for:",
        options: [
          "Soft tissue abscess",
          "Complex pelvic fractures",
          "muscle strain",
          "Herniated disc"
        ],
        answer: "Complex pelvic fractures"
      },
      {
        id: 4,
        question: "Q4. What is the typical scan range for a CT bony pelvis?​",
        options: [
          "Thoracic inlet to diaphragm​",
          "From iliac crests to ischial tuberosities",
          "Lower limbs only",
          "Cervical spine"
        ],
        answer: "lliac crests to ischial tuberosities"
      },
      {
        id: 5,
        question: "Q5. In trauma, CT bony pelvis is essential for identifying:​",
        options: [
          "Muscle tears​",
          "Sacral fractures",
          "Appendicitis​",
          "Bladder stones"
        ],
        answer: "Sacral fractures"
      },
      {
        id: 6,
        question: "Q6. When is contrast typically used in a bony pelvis CT?​",
        options: [
          "Bone tumor",
          "Pelvic fracture​ ",
          "Suspected infection or tumor extension",
          "Osteoarthritis"
        ],
        answer: "Suspected infection or tumor extension"
      },
      {
        id: 7,
        question: "Q7.  What is a common cause of pelvic ring disruption?​",
        options: [
          "High-energy trauma (e.g., MVA) ​",
          "Stroke​",
          "Appendicitis​",
          "Osteroarthritis"
        ],
        answer: "High-energy trauma (e.g., MVA)"
      },
      {
        id: 8,
        question: "Q8. The acetabulum is part of the:​",
        options: [
          "Sacrum​",
          "Ischium only",
          "Hip joint(formed by ilium, ischium, and pubis)",
          "Femur"
        ],
        answer: "Hip joint(formed by ilium, ischium, and pubis)"
      },
      {
        id: 9,
        question: "Q9. A vertical shear injury of the pelvis typically results in:​",
        options: [
          "Anterior subluxation",
          "Asymmetric elevation of a hemipelvis ",
          "Herniation​",
          "Bladder rupture"
        ],
        answer: "Asymmetric elevation of a hemipelvis"
      },
      {
        id: 10,
        question: "Q10. What window setting is best for bone evaluation in CT?​",
        options: [
          "Brain",
          "Soft tissue",
          "Lung",
          "Bone window",
        ],
        answer: "Bone window"
      },
      {
        id: 11,
        question: "Q11. Which of these fractures is commonly associated with pelvic trauma?​",
        options: [
          "Jefferson fracture​",
          "Open book fracture",
          "Hangman's fracture",
          "Le Fort fracture"
        ],
        answer: "Open book fracture"
      },
      {
        id: 12,
        question: "Q12. Which pelvic structure articulates with the femur?​",
        options: [
          "Acetabulum",
          "lliac crest",
          "Sacral ala",
          "Public symphysis"
        ],
        answer: "Acetabulum"
      },
      {
        id: 13,
        question: "Q13. Which imaging plane is most useful for assessing pubic rami fractures?​",
        options: [
          "Axial only",
          "Coronal reformals",
          "Oblique",
          "Lateral"
        ],
        answer: "Coronal reformals"
      },
      {
        id: 14,
        question: "Q14. The sacroiliac joint is located between:",
        options: [
          "llium and acetabulum",
          "llium and sacrum",
          "Pubis and ischium",
          "Femur and tibia"
        ],
        answer: "llium and sacrum"
      },
      {
        id: 15,
        question: "Q15. In trauma, a widened pubic symphysis suggests:​",
        options: [
          "Osteitis",
          "Pelvic ring instability",
          "Scolliosis",
          "Normal variant"
        ],
        answer: "Pelvic ring instability"
      },
      {
        id: 16,
        question: "Q16. What CT finding indicates pelvic hemorrhage?​",
        options: [
          "Soft tissue swelling​",
          "Contrast extravasation in arterial pelvis",
          "Fat stranding",
          "Gas in joint space"
        ],
        answer: "Contrast extravasation in arterial pelvis"
      },
      {
        id: 17,
        question: "Q17. Which bone forms the posterior portion of the pelvic ring?​",
        options: [
          "Pubis",
          "llium",
          "Sacrum",
          "Ischium"
        ],
        answer: "Sacrum"
      },
      {
        id: 18,
        question: "Q18. What is a benefit of CT over plain X-ray in pelvis imaging?",
        options: [
          "Cheaper",
          "3D fracture mapping",
          "Lower radiation",
          "Better soft tissue contrast than MRI"
        ],
        answer: "3D fracture mapping"
      },
      {
        id: 19,
        question: "Q19. What is the primary concern in unstable pelvic fractures?​",
        options: [
          "Nerve pain",
          "Soft tissue swelling",
          "Hemorrhage and organ injury",
          "Muscle weakness"
        ],
        answer: "Hemorrhage and organ injury"
      },
      {
        id: 20,
        question: "Q20. Which of these bones is not part of the pelvic girdle?​",
        options: [
          "llium",
          "Femur",
          "Ischium",
          "Pubis"
        ],
        answer: "Femur"
      },
      {
        id: 21,
        question: "Q21. The pelvic brim divides the pelvis into:",
        options: [
          "Right and left halves",
          "Posterior and anterior portions",
          "True and false pelvis",
          "Outer and inner bones"
        ],
        answer: "True and false pelvis"
      },
      {
        id: 22,
        question: "Q22. Which finding on CT suggests pelvic osteomyelitis?​",
        options: [
          "Joint dislocation",
          "Lytic bone lesions with sift tissue changes",
          "Dense sclerosis",
          "Pubic diastasis"
        ],
        answer: "Lytic bone lesions with sift tissue changes"
      },
      {
        id: 23,
        question: "Q23. What is the role of contrast in CT pelvis for tumors?​",
        options: [
          "Outlines bones",
          "Highlight bones",
          "Enhances vascular supply and soft tissue planes",
          "Reduce noise"
        ],
        answer: "Enhances vascular supply and soft tissue planes"
      },
      {
        id: 24,
        question: "Q24. Which fracture type involves the sacroiliac joint and iliac wing?​",
        options: [
          "Le Fort",
          "Hungman's",
          "Crescent fracture",
          "Boxer's fracture"
        ],
        answer: "Crescent fracture"
      },
      {
        id: 25,
        question: "Q25. What is a contraindication for IV contrast in CT pelvis?​?​",
        options: [
          "fracture",
          "Haematuria",
          "Renal failure",
          "Pelvic pain"
        ],
        answer: "Renal failure"
      },
      {
        id: 26,
        question: "Q26. What technique can reduce dose in pelvic CT?​​ ",
        options: [
          "increase kVp",
          "Manual mA setting",
          "Automatic exposure control (AEC)",
          "Use contrast only"
        ],
        answer: "Automatic exposure control (AEC)"
      },
      {
        id: 27,
        question: "Q27. What does metal artifact reduction help with?​",
        options: [
          "Prosthesis or fixation hardware clarity ​",
          "Increasing dose",
          "Changing Slice thickness",
          "Enhancing fat planes"
        ],
        answer: "Prosthesis or fixation hardware clarity"
      },
      {
        id: 28,
        question: "Q28. Sacral insufficiency fractures often occur in:​",
        options: [
          "Children",
          "Athletes",
          "Elderly with osteoporosis",
          "Soldiers"
        ],
        answer: "Elderly with osteoporosis"
      },
      {
        id: 29,
        question: "Q29. Which part of the pelvis is involved in a straddle fracture?​",
        options: [
          "lliac wings",
          "Acetabulum",
          "Both superior and inferior pubic rami bilateral",
          "Sacrum only"
        ],
        answer: "Both superior and inferior pubic rami bilateral"
      },
      {
        id: 30,
        question: "Q30. What is the main use of 3D reconstruction in pelvic CT?​",
        options: [
          "Showing muscle tears",
          "Surgical planning and fracture visualization ",
          "Comparing organs",
          "Measuring fat"
        ],
        answer: "Surgical planning and fracture visualization "
      }
    ]
  },
  "501": {
  title: "CT Cervical Spine",
  description: "Assess your understanding of CT thoracic spine indications, anatomy, and techniques.",
  questions: [
    {
      id: 1,
      question: "Q1. What is the primary indication for a CT thoracic spine study?",
      options: ["Appendicitis", "Spinal trauma", "Stroke", "Gallstones"],
      answer: "Spinal trauma"
    },
    {
      id: 2,
      question: "Q2. Which patient position is typically used for CT thoracic spine scanning?",
      options: ["Prone", "Supine", "Lateral decubitus", "Standing"],
      answer: "Supine"
    },
    {
      id: 3,
      question: "Q3. What is the typical scan range for a CT thoracic spine?",
      options: ["T1 to T12", "C1 to T1", "T12 to L5", "T1 to L1"],
      answer: "T1 to T12"
    },
    {
      id: 4,
      question: "Q4. Which plane is most commonly used for reviewing CT thoracic spine images?",
      options: ["Coronal", "Sagittal", "Axial", "Oblique"],
      answer: "Axial"
    },
    {
      id: 5,
      question: "Q5. Why might contrast be used in a CT thoracic spine study?",
      options: [
        "To better see bones",
        "To evaluate soft tissue or infection",
        "To reduce radiation dose",
        "To shorten scan time"
      ],
      answer: "To evaluate soft tissue or infection"
    },
    {
      id: 6,
      question: "Q6. Which condition is best evaluated by CT over MRI for the thoracic spine?",
      options: ["Soft tissue tumors", "Vertebral fractures", "Disc herniation", "Spinal cord edema"],
      answer: "Vertebral fractures"
    },
    {
      id: 7,
      question: "Q7. What type of detector is used in modern CT scanners for spine studies?",
      options: ["Single-slice", "Flat-panel", "Multidetector", "Analog detector"],
      answer: "Multidetector"
    },
    {
      id: 8,
      question: "Q8. Which window setting is ideal for bone evaluation in thoracic spine CT?",
      options: ["Brain window", "Lung window", "Soft tissue window", "Bone window"],
      answer: "Bone window"
    },
    {
      id: 9,
      question: "Q9. How is patient motion minimized during a thoracic spine CT scan?",
      options: [
        "Breathing exercises",
        "Immobilization and breath-hold instructions",
        "Sedation only",
        "Continuous movement"
      ],
      answer: "Immobilization and breath-hold instructions"
    },
    {
      id: 10,
      question: "Q10. What does a comminuted fracture indicate on CT?",
      options: [
        "Single fracture line",
        "Clean separation",
        "Bone shattered into multiple pieces",
        "Bone thinning"
      ],
      answer: "Bone shattered into multiple pieces"
    },
    {
  id: 11,
  question: "Q11. Which artifact is common in thoracic spine CT?",
  options: ["Beam hardening", "Ring artifact", "Motion artifact", "All of the above"],
  answer: "All of the above"
},
{
  id: 12,
  question: "Q12. What is the advantage of MPR in thoracic spine CT?",
  options: ["Faster scanning", "Less radiation", "Better anatomical orientation", "Cheaper procedure"],
  answer: "Better anatomical orientation"
},
{
  id: 13,
  question: "Q13. When is CT thoracic spine with contrast typically indicated?",
  options: [
    "Acute trauma",
    "Suspected infection or tumor",
    "Routine scoliosis",
    "Degenerative disc disease"
  ],
  answer: "Suspected infection or tumor"
},
{
  id: 14,
  question: "Q14. What anatomy is primarily evaluated in thoracic spine CT?",
  options: [
    "Intercostal vessels",
    "Ribs",
    "Vertebral bodies and spinal canal",
    "Diaphragm"
  ],
  answer: "Vertebral bodies and spinal canal"
},
{
  id: 15,
  question: "Q15. What slice thickness is commonly used for thoracic spine CT?",
  options: ["10 mm", "0.5 mm", "5 mm", "20 mm"],
  answer: "5 mm"
},
{
  id: 16,
  question: "Q16. Which pathology is better identified by CT than MRI in thoracic spine?",
  options: ["Cord edema", "Disc herniation", "Osteophyte formation", "Syringomyelia"],
  answer: "Osteophyte formation"
},
{
  id: 17,
  question: "Q17. Why is scout imaging done before CT thoracic spine?",
  options: [
    "Dose calculation",
    "Patient registration",
    "Planning the scan range",
    "3D rendering"
  ],
  answer: "Planning the scan range"
},
{
  id: 18,
  question: "Q18. Which condition may show lytic lesions in the thoracic vertebrae?",
  options: ["Osteoarthritis", "Metastasis", "Ankylosing spondylitis", "Discitis"],
  answer: "Metastasis"
},
{
  id: 19,
  question: "Q19. How can radiation dose be reduced in CT spine studies?",
  options: [
    "Increase tube current",
    "Increase scan length",
    "Use automatic exposure control",
    "Remove filters"
  ],
  answer: "Use automatic exposure control"
},
{
  id: 20,
  question: "Q20. Which plane is best for assessing spinal alignment?",
  options: ["Axial", "Coronal", "Sagittal", "Oblique"],
  answer: "Sagittal"
},
{
  id: 21,
  question: "Q21. Which imaging technique is often paired with CT for soft tissue detail?",
  options: ["PET", "Ultrasound", "MRI", "X-ray"],
  answer: "MRI"
},
{
  id: 22,
  question: "Q22. Which vertebra is the transition point between cervical and thoracic spine?",
  options: ["C7", "T1", "T12", "C6"],
  answer: "T1"
},
{
  id: 23,
  question: "Q23. How does CT identify compression fractures?",
  options: [
    "Displacement of ribs",
    "Loss of vertebral height",
    "Increased spinal cord size",
    "Soft tissue swelling"
  ],
  answer: "Loss of vertebral height"
},
{
  id: 24,
  question: "Q24. Which pathology is best seen with contrast CT of thoracic spine?",
  options: ["Fractures", "Scoliosis", "Epidural abscess", "Osteophytes"],
  answer: "Epidural abscess"
},
{
  id: 25,
  question: "Q25. CT thoracic spine can help diagnose which post-surgical complication?",
  options: ["Disc bulge", "Hardware loosening", "Spinal cord tumor", "Syrinx"],
  answer: "Hardware loosening"
},
{
  id: 26,
  question: "Q26. Which part of the vertebra is typically fractured in trauma?",
  options: ["Lamina", "Pedicle", "Vertebral body", "Transverse process"],
  answer: "Vertebral body"
},
{
  id: 27,
  question: "Q27. Which structure can be compressed in thoracic spine trauma?",
  options: ["Trachea", "Aorta", "Spinal cord", "Esophagus"],
  answer: "Spinal cord"
},
{
  id: 28,
  question: "Q28. What contrast agent is used in contrast CT studies?",
  options: ["Barium sulfate", "Gadolinium", "Iodinated contrast", "Saline"],
  answer: "Iodinated contrast"
},
{
  id: 29,
  question: "Q29. CT thoracic spine is useful in evaluating which post-cancer symptom?",
  options: ["Cough", "Limb weakness", "Jaundice", "Visual disturbances"],
  answer: "Limb weakness"
},
{
  id: 30,
  question: "Q30. Which CT post-processing method enhances bony detail visualization?",
  options: ["MIP", "MinIP", "Bone algorithm", "VR"],
  answer: "Bone algorithm"
}


  ]
  },
  "502": {
  title: "CT Thoracic Spine Quiz",
  description: "Assess your understanding of CT thoracic spine indications, anatomy, and techniques.",
  questions: [
    {
      id: 1,
      question: "Q1. What is the primary indication for a CT thoracic spine study?",
      options: ["Appendicitis", "Spinal trauma", "Stroke", "Gallstones"],
      answer: "Spinal trauma"
    },
    {
      id: 2,
      question: "Q2. Which patient position is typically used for CT thoracic spine scanning?",
      options: ["Prone", "Supine", "Lateral decubitus", "Standing"],
      answer: "Supine"
    },
    {
      id: 3,
      question: "Q3. What is the typical scan range for a CT thoracic spine?",
      options: ["T1 to T12", "C1 to T1", "T12 to L5", "T1 to L1"],
      answer: "T1 to T12"
    },
    {
      id: 4,
      question: "Q4. Which plane is most commonly used for reviewing CT thoracic spine images?",
      options: ["Coronal", "Sagittal", "Axial", "Oblique"],
      answer: "Axial"
    },
    {
      id: 5,
      question: "Q5. Why might contrast be used in a CT thoracic spine study?",
      options: [
        "To better see bones",
        "To evaluate soft tissue or infection",
        "To reduce radiation dose",
        "To shorten scan time"
      ],
      answer: "To evaluate soft tissue or infection"
    },
    {
      id: 6,
      question: "Q6. Which condition is best evaluated by CT over MRI for the thoracic spine?",
      options: ["Soft tissue tumors", "Vertebral fractures", "Disc herniation", "Spinal cord edema"],
      answer: "Vertebral fractures"
    },
    {
      id: 7,
      question: "Q7. What type of detector is used in modern CT scanners for spine studies?",
      options: ["Single-slice", "Flat-panel", "Multidetector", "Analog detector"],
      answer: "Multidetector"
    },
    {
      id: 8,
      question: "Q8. Which window setting is ideal for bone evaluation in thoracic spine CT?",
      options: ["Brain window", "Lung window", "Soft tissue window", "Bone window"],
      answer: "Bone window"
    },
    {
      id: 9,
      question: "Q9. How is patient motion minimized during a thoracic spine CT scan?",
      options: [
        "Breathing exercises",
        "Immobilization and breath-hold instructions",
        "Sedation only",
        "Continuous movement"
      ],
      answer: "Immobilization and breath-hold instructions"
    },
    {
      id: 10,
      question: "Q10. What does a comminuted fracture indicate on CT?",
      options: [
        "Single fracture line",
        "Clean separation",
        "Bone shattered into multiple pieces",
        "Bone thinning"
      ],
      answer: "Bone shattered into multiple pieces"
    },
    {
  id: 11,
  question: "Q11. Which artifact is common in thoracic spine CT?",
  options: ["Beam hardening", "Ring artifact", "Motion artifact", "All of the above"],
  answer: "All of the above"
},
{
  id: 12,
  question: "Q12. What is the advantage of MPR in thoracic spine CT?",
  options: ["Faster scanning", "Less radiation", "Better anatomical orientation", "Cheaper procedure"],
  answer: "Better anatomical orientation"
},
{
  id: 13,
  question: "Q13. When is CT thoracic spine with contrast typically indicated?",
  options: [
    "Acute trauma",
    "Suspected infection or tumor",
    "Routine scoliosis",
    "Degenerative disc disease"
  ],
  answer: "Suspected infection or tumor"
},
{
  id: 14,
  question: "Q14. What anatomy is primarily evaluated in thoracic spine CT?",
  options: [
    "Intercostal vessels",
    "Ribs",
    "Vertebral bodies and spinal canal",
    "Diaphragm"
  ],
  answer: "Vertebral bodies and spinal canal"
},
{
  id: 15,
  question: "Q15. What slice thickness is commonly used for thoracic spine CT?",
  options: ["10 mm", "0.5 mm", "5 mm", "20 mm"],
  answer: "5 mm"
},
{
  id: 16,
  question: "Q16. Which pathology is better identified by CT than MRI in thoracic spine?",
  options: ["Cord edema", "Disc herniation", "Osteophyte formation", "Syringomyelia"],
  answer: "Osteophyte formation"
},
{
  id: 17,
  question: "Q17. Why is scout imaging done before CT thoracic spine?",
  options: [
    "Dose calculation",
    "Patient registration",
    "Planning the scan range",
    "3D rendering"
  ],
  answer: "Planning the scan range"
},
{
  id: 18,
  question: "Q18. Which condition may show lytic lesions in the thoracic vertebrae?",
  options: ["Osteoarthritis", "Metastasis", "Ankylosing spondylitis", "Discitis"],
  answer: "Metastasis"
},
{
  id: 19,
  question: "Q19. How can radiation dose be reduced in CT spine studies?",
  options: [
    "Increase tube current",
    "Increase scan length",
    "Use automatic exposure control",
    "Remove filters"
  ],
  answer: "Use automatic exposure control"
},
{
  id: 20,
  question: "Q20. Which plane is best for assessing spinal alignment?",
  options: ["Axial", "Coronal", "Sagittal", "Oblique"],
  answer: "Sagittal"
},
{
  id: 21,
  question: "Q21. Which imaging technique is often paired with CT for soft tissue detail?",
  options: ["PET", "Ultrasound", "MRI", "X-ray"],
  answer: "MRI"
},
{
  id: 22,
  question: "Q22. Which vertebra is the transition point between cervical and thoracic spine?",
  options: ["C7", "T1", "T12", "C6"],
  answer: "T1"
},
{
  id: 23,
  question: "Q23. How does CT identify compression fractures?",
  options: [
    "Displacement of ribs",
    "Loss of vertebral height",
    "Increased spinal cord size",
    "Soft tissue swelling"
  ],
  answer: "Loss of vertebral height"
},
{
  id: 24,
  question: "Q24. Which pathology is best seen with contrast CT of thoracic spine?",
  options: ["Fractures", "Scoliosis", "Epidural abscess", "Osteophytes"],
  answer: "Epidural abscess"
},
{
  id: 25,
  question: "Q25. CT thoracic spine can help diagnose which post-surgical complication?",
  options: ["Disc bulge", "Hardware loosening", "Spinal cord tumor", "Syrinx"],
  answer: "Hardware loosening"
},
{
  id: 26,
  question: "Q26. Which part of the vertebra is typically fractured in trauma?",
  options: ["Lamina", "Pedicle", "Vertebral body", "Transverse process"],
  answer: "Vertebral body"
},
{
  id: 27,
  question: "Q27. Which structure can be compressed in thoracic spine trauma?",
  options: ["Trachea", "Aorta", "Spinal cord", "Esophagus"],
  answer: "Spinal cord"
},
{
  id: 28,
  question: "Q28. What contrast agent is used in contrast CT studies?",
  options: ["Barium sulfate", "Gadolinium", "Iodinated contrast", "Saline"],
  answer: "Iodinated contrast"
},
{
  id: 29,
  question: "Q29. CT thoracic spine is useful in evaluating which post-cancer symptom?",
  options: ["Cough", "Limb weakness", "Jaundice", "Visual disturbances"],
  answer: "Limb weakness"
},
{
  id: 30,
  question: "Q30. Which CT post-processing method enhances bony detail visualization?",
  options: ["MIP", "MinIP", "Bone algorithm", "VR"],
  answer: "Bone algorithm"
}


  ]
  },
  "503": {
  title: "CT Lumbar Spine",
  description: "Assess your understanding of CT thoracic spine indications, anatomy, and techniques.",
  questions: [
    {
      id: 1,
      question: "Q1. What is the primary indication for a CT thoracic spine study?",
      options: ["Appendicitis", "Spinal trauma", "Stroke", "Gallstones"],
      answer: "Spinal trauma"
    },
    {
      id: 2,
      question: "Q2. Which patient position is typically used for CT thoracic spine scanning?",
      options: ["Prone", "Supine", "Lateral decubitus", "Standing"],
      answer: "Supine"
    },
    {
      id: 3,
      question: "Q3. What is the typical scan range for a CT thoracic spine?",
      options: ["T1 to T12", "C1 to T1", "T12 to L5", "T1 to L1"],
      answer: "T1 to T12"
    },
    {
      id: 4,
      question: "Q4. Which plane is most commonly used for reviewing CT thoracic spine images?",
      options: ["Coronal", "Sagittal", "Axial", "Oblique"],
      answer: "Axial"
    },
    {
      id: 5,
      question: "Q5. Why might contrast be used in a CT thoracic spine study?",
      options: [
        "To better see bones",
        "To evaluate soft tissue or infection",
        "To reduce radiation dose",
        "To shorten scan time"
      ],
      answer: "To evaluate soft tissue or infection"
    },
    {
      id: 6,
      question: "Q6. Which condition is best evaluated by CT over MRI for the thoracic spine?",
      options: ["Soft tissue tumors", "Vertebral fractures", "Disc herniation", "Spinal cord edema"],
      answer: "Vertebral fractures"
    },
    {
      id: 7,
      question: "Q7. What type of detector is used in modern CT scanners for spine studies?",
      options: ["Single-slice", "Flat-panel", "Multidetector", "Analog detector"],
      answer: "Multidetector"
    },
    {
      id: 8,
      question: "Q8. Which window setting is ideal for bone evaluation in thoracic spine CT?",
      options: ["Brain window", "Lung window", "Soft tissue window", "Bone window"],
      answer: "Bone window"
    },
    {
      id: 9,
      question: "Q9. How is patient motion minimized during a thoracic spine CT scan?",
      options: [
        "Breathing exercises",
        "Immobilization and breath-hold instructions",
        "Sedation only",
        "Continuous movement"
      ],
      answer: "Immobilization and breath-hold instructions"
    },
    {
      id: 10,
      question: "Q10. What does a comminuted fracture indicate on CT?",
      options: [
        "Single fracture line",
        "Clean separation",
        "Bone shattered into multiple pieces",
        "Bone thinning"
      ],
      answer: "Bone shattered into multiple pieces"
    },
    {
  id: 11,
  question: "Q11. Which artifact is common in thoracic spine CT?",
  options: ["Beam hardening", "Ring artifact", "Motion artifact", "All of the above"],
  answer: "All of the above"
},
{
  id: 12,
  question: "Q12. What is the advantage of MPR in thoracic spine CT?",
  options: ["Faster scanning", "Less radiation", "Better anatomical orientation", "Cheaper procedure"],
  answer: "Better anatomical orientation"
},
{
  id: 13,
  question: "Q13. When is CT thoracic spine with contrast typically indicated?",
  options: [
    "Acute trauma",
    "Suspected infection or tumor",
    "Routine scoliosis",
    "Degenerative disc disease"
  ],
  answer: "Suspected infection or tumor"
},
{
  id: 14,
  question: "Q14. What anatomy is primarily evaluated in thoracic spine CT?",
  options: [
    "Intercostal vessels",
    "Ribs",
    "Vertebral bodies and spinal canal",
    "Diaphragm"
  ],
  answer: "Vertebral bodies and spinal canal"
},
{
  id: 15,
  question: "Q15. What slice thickness is commonly used for thoracic spine CT?",
  options: ["10 mm", "0.5 mm", "5 mm", "20 mm"],
  answer: "5 mm"
},
{
  id: 16,
  question: "Q16. Which pathology is better identified by CT than MRI in thoracic spine?",
  options: ["Cord edema", "Disc herniation", "Osteophyte formation", "Syringomyelia"],
  answer: "Osteophyte formation"
},
{
  id: 17,
  question: "Q17. Why is scout imaging done before CT thoracic spine?",
  options: [
    "Dose calculation",
    "Patient registration",
    "Planning the scan range",
    "3D rendering"
  ],
  answer: "Planning the scan range"
},
{
  id: 18,
  question: "Q18. Which condition may show lytic lesions in the thoracic vertebrae?",
  options: ["Osteoarthritis", "Metastasis", "Ankylosing spondylitis", "Discitis"],
  answer: "Metastasis"
},
{
  id: 19,
  question: "Q19. How can radiation dose be reduced in CT spine studies?",
  options: [
    "Increase tube current",
    "Increase scan length",
    "Use automatic exposure control",
    "Remove filters"
  ],
  answer: "Use automatic exposure control"
},
{
  id: 20,
  question: "Q20. Which plane is best for assessing spinal alignment?",
  options: ["Axial", "Coronal", "Sagittal", "Oblique"],
  answer: "Sagittal"
},
{
  id: 21,
  question: "Q21. Which imaging technique is often paired with CT for soft tissue detail?",
  options: ["PET", "Ultrasound", "MRI", "X-ray"],
  answer: "MRI"
},
{
  id: 22,
  question: "Q22. Which vertebra is the transition point between cervical and thoracic spine?",
  options: ["C7", "T1", "T12", "C6"],
  answer: "T1"
},
{
  id: 23,
  question: "Q23. How does CT identify compression fractures?",
  options: [
    "Displacement of ribs",
    "Loss of vertebral height",
    "Increased spinal cord size",
    "Soft tissue swelling"
  ],
  answer: "Loss of vertebral height"
},
{
  id: 24,
  question: "Q24. Which pathology is best seen with contrast CT of thoracic spine?",
  options: ["Fractures", "Scoliosis", "Epidural abscess", "Osteophytes"],
  answer: "Epidural abscess"
},
{
  id: 25,
  question: "Q25. CT thoracic spine can help diagnose which post-surgical complication?",
  options: ["Disc bulge", "Hardware loosening", "Spinal cord tumor", "Syrinx"],
  answer: "Hardware loosening"
},
{
  id: 26,
  question: "Q26. Which part of the vertebra is typically fractured in trauma?",
  options: ["Lamina", "Pedicle", "Vertebral body", "Transverse process"],
  answer: "Vertebral body"
},
{
  id: 27,
  question: "Q27. Which structure can be compressed in thoracic spine trauma?",
  options: ["Trachea", "Aorta", "Spinal cord", "Esophagus"],
  answer: "Spinal cord"
},
{
  id: 28,
  question: "Q28. What contrast agent is used in contrast CT studies?",
  options: ["Barium sulfate", "Gadolinium", "Iodinated contrast", "Saline"],
  answer: "Iodinated contrast"
},
{
  id: 29,
  question: "Q29. CT thoracic spine is useful in evaluating which post-cancer symptom?",
  options: ["Cough", "Limb weakness", "Jaundice", "Visual disturbances"],
  answer: "Limb weakness"
},
{
  id: 30,
  question: "Q30. Which CT post-processing method enhances bony detail visualization?",
  options: ["MIP", "MinIP", "Bone algorithm", "VR"],
  answer: "Bone algorithm"
}


  ]
  },
  "504": {
  title: "CT Sacrum & Coccyx",
  description: "Assess your understanding of CT thoracic spine indications, anatomy, and techniques.",
  questions: [
    {
      id: 1,
      question: "Q1. What is the primary indication for a CT thoracic spine study?",
      options: ["Appendicitis", "Spinal trauma", "Stroke", "Gallstones"],
      answer: "Spinal trauma"
    },
    {
      id: 2,
      question: "Q2. Which patient position is typically used for CT thoracic spine scanning?",
      options: ["Prone", "Supine", "Lateral decubitus", "Standing"],
      answer: "Supine"
    },
    {
      id: 3,
      question: "Q3. What is the typical scan range for a CT thoracic spine?",
      options: ["T1 to T12", "C1 to T1", "T12 to L5", "T1 to L1"],
      answer: "T1 to T12"
    },
    {
      id: 4,
      question: "Q4. Which plane is most commonly used for reviewing CT thoracic spine images?",
      options: ["Coronal", "Sagittal", "Axial", "Oblique"],
      answer: "Axial"
    },
    {
      id: 5,
      question: "Q5. Why might contrast be used in a CT thoracic spine study?",
      options: [
        "To better see bones",
        "To evaluate soft tissue or infection",
        "To reduce radiation dose",
        "To shorten scan time"
      ],
      answer: "To evaluate soft tissue or infection"
    },
    {
      id: 6,
      question: "Q6. Which condition is best evaluated by CT over MRI for the thoracic spine?",
      options: ["Soft tissue tumors", "Vertebral fractures", "Disc herniation", "Spinal cord edema"],
      answer: "Vertebral fractures"
    },
    {
      id: 7,
      question: "Q7. What type of detector is used in modern CT scanners for spine studies?",
      options: ["Single-slice", "Flat-panel", "Multidetector", "Analog detector"],
      answer: "Multidetector"
    },
    {
      id: 8,
      question: "Q8. Which window setting is ideal for bone evaluation in thoracic spine CT?",
      options: ["Brain window", "Lung window", "Soft tissue window", "Bone window"],
      answer: "Bone window"
    },
    {
      id: 9,
      question: "Q9. How is patient motion minimized during a thoracic spine CT scan?",
      options: [
        "Breathing exercises",
        "Immobilization and breath-hold instructions",
        "Sedation only",
        "Continuous movement"
      ],
      answer: "Immobilization and breath-hold instructions"
    },
    {
      id: 10,
      question: "Q10. What does a comminuted fracture indicate on CT?",
      options: [
        "Single fracture line",
        "Clean separation",
        "Bone shattered into multiple pieces",
        "Bone thinning"
      ],
      answer: "Bone shattered into multiple pieces"
    },
    {
  id: 11,
  question: "Q11. Which artifact is common in thoracic spine CT?",
  options: ["Beam hardening", "Ring artifact", "Motion artifact", "All of the above"],
  answer: "All of the above"
},
{
  id: 12,
  question: "Q12. What is the advantage of MPR in thoracic spine CT?",
  options: ["Faster scanning", "Less radiation", "Better anatomical orientation", "Cheaper procedure"],
  answer: "Better anatomical orientation"
},
{
  id: 13,
  question: "Q13. When is CT thoracic spine with contrast typically indicated?",
  options: [
    "Acute trauma",
    "Suspected infection or tumor",
    "Routine scoliosis",
    "Degenerative disc disease"
  ],
  answer: "Suspected infection or tumor"
},
{
  id: 14,
  question: "Q14. What anatomy is primarily evaluated in thoracic spine CT?",
  options: [
    "Intercostal vessels",
    "Ribs",
    "Vertebral bodies and spinal canal",
    "Diaphragm"
  ],
  answer: "Vertebral bodies and spinal canal"
},
{
  id: 15,
  question: "Q15. What slice thickness is commonly used for thoracic spine CT?",
  options: ["10 mm", "0.5 mm", "5 mm", "20 mm"],
  answer: "5 mm"
},
{
  id: 16,
  question: "Q16. Which pathology is better identified by CT than MRI in thoracic spine?",
  options: ["Cord edema", "Disc herniation", "Osteophyte formation", "Syringomyelia"],
  answer: "Osteophyte formation"
},
{
  id: 17,
  question: "Q17. Why is scout imaging done before CT thoracic spine?",
  options: [
    "Dose calculation",
    "Patient registration",
    "Planning the scan range",
    "3D rendering"
  ],
  answer: "Planning the scan range"
},
{
  id: 18,
  question: "Q18. Which condition may show lytic lesions in the thoracic vertebrae?",
  options: ["Osteoarthritis", "Metastasis", "Ankylosing spondylitis", "Discitis"],
  answer: "Metastasis"
},
{
  id: 19,
  question: "Q19. How can radiation dose be reduced in CT spine studies?",
  options: [
    "Increase tube current",
    "Increase scan length",
    "Use automatic exposure control",
    "Remove filters"
  ],
  answer: "Use automatic exposure control"
},
{
  id: 20,
  question: "Q20. Which plane is best for assessing spinal alignment?",
  options: ["Axial", "Coronal", "Sagittal", "Oblique"],
  answer: "Sagittal"
},
{
  id: 21,
  question: "Q21. Which imaging technique is often paired with CT for soft tissue detail?",
  options: ["PET", "Ultrasound", "MRI", "X-ray"],
  answer: "MRI"
},
{
  id: 22,
  question: "Q22. Which vertebra is the transition point between cervical and thoracic spine?",
  options: ["C7", "T1", "T12", "C6"],
  answer: "T1"
},
{
  id: 23,
  question: "Q23. How does CT identify compression fractures?",
  options: [
    "Displacement of ribs",
    "Loss of vertebral height",
    "Increased spinal cord size",
    "Soft tissue swelling"
  ],
  answer: "Loss of vertebral height"
},
{
  id: 24,
  question: "Q24. Which pathology is best seen with contrast CT of thoracic spine?",
  options: ["Fractures", "Scoliosis", "Epidural abscess", "Osteophytes"],
  answer: "Epidural abscess"
},
{
  id: 25,
  question: "Q25. CT thoracic spine can help diagnose which post-surgical complication?",
  options: ["Disc bulge", "Hardware loosening", "Spinal cord tumor", "Syrinx"],
  answer: "Hardware loosening"
},
{
  id: 26,
  question: "Q26. Which part of the vertebra is typically fractured in trauma?",
  options: ["Lamina", "Pedicle", "Vertebral body", "Transverse process"],
  answer: "Vertebral body"
},
{
  id: 27,
  question: "Q27. Which structure can be compressed in thoracic spine trauma?",
  options: ["Trachea", "Aorta", "Spinal cord", "Esophagus"],
  answer: "Spinal cord"
},
{
  id: 28,
  question: "Q28. What contrast agent is used in contrast CT studies?",
  options: ["Barium sulfate", "Gadolinium", "Iodinated contrast", "Saline"],
  answer: "Iodinated contrast"
},
{
  id: 29,
  question: "Q29. CT thoracic spine is useful in evaluating which post-cancer symptom?",
  options: ["Cough", "Limb weakness", "Jaundice", "Visual disturbances"],
  answer: "Limb weakness"
},
{
  id: 30,
  question: "Q30. Which CT post-processing method enhances bony detail visualization?",
  options: ["MIP", "MinIP", "Bone algorithm", "VR"],
  answer: "Bone algorithm"
}


  ]
  },
};

const Quiz = () => {
  const { courseId, topicId } = useParams();  
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();

  
  // Get registration_id from location state
  const registrationId = location.state?.registration_id || "";
  const teacher_username = location.state?.teacher_username;  

  // Get current course data
  const currentCourse = courseQuestions[courseId] || {
    title: "Quiz Not Available",
    description: "No quiz available for this course.",
    questions: []
  };

  const handleAnswerChange = (questionId, selectedOption) => {
    setAnswers(prev => {
      // If the question has multiple answers (array), handle multiple selection
      const question = currentCourse.questions.find(q => q.id === questionId);
      if (Array.isArray(question.answer)) {
        const prevAnswers = prev[questionId] || [];
        if (prevAnswers.includes(selectedOption)) {
          return {
            ...prev,
            [questionId]: prevAnswers.filter(opt => opt !== selectedOption)
          };
        } else {
          return {
            ...prev,
            [questionId]: [...prevAnswers, selectedOption]
          };
        }
      }
      // For single answer questions
      return {
        ...prev,
        [questionId]: selectedOption
      };
    });
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const totalQuestions = currentCourse.questions.length;
    const answeredQuestions = Object.keys(answers).length;

    if (answeredQuestions < totalQuestions) {
      alert(`Please answer all questions before submitting. (${answeredQuestions}/${totalQuestions} answered)`);
      return;
    }

    setSubmitted(true);

    // Calculate results
    let correctAnswers = 0;
    const results = currentCourse.questions.map(question => {
      let isCorrect;
      if (Array.isArray(question.answer)) {
        // For multiple answer questions, check if arrays match exactly
        const userAnswerSet = new Set(answers[question.id] || []);
        const correctAnswerSet = new Set(question.answer);
        isCorrect = userAnswerSet.size === correctAnswerSet.size && 
          [...userAnswerSet].every(answer => correctAnswerSet.has(answer));
      } else {
        // For single answer questions
        isCorrect = answers[question.id] === question.answer;
      }
      if (isCorrect) correctAnswers++;
      return {
        question: question.question,
        userAnswer: answers[question.id],
        correctAnswer: question.answer,
        isCorrect
      };
    });

    const percentage = Math.round((correctAnswers / totalQuestions) * 100);

    try {
    const payload = { 
       registration_id: registrationId, 
       username:        user.username, 
       teacher_username: teacher_username,
       topic_id:        parseInt(topicId, 10), 
       course_id:       parseInt(courseId, 10), 
       correct_count:   correctAnswers, 
       total_questions: totalQuestions, 
       percentage, 
     }; 
     console.log('Submitting quiz-results payload:', payload);
     const resp = await fetch( 
       `${process.env.REACT_APP_API_URL}/api/quiz-results`, 
       { 
         method: 'POST', 
         headers: { 'Content-Type': 'application/json' }, 
         body: JSON.stringify(payload) 
       } 
     ); 
     if (!resp.ok) throw new Error(await resp.text()); 
   } catch (err) { 
     console.error('Failed to save quiz results:', err); 
     alert('Could not save your quiz — please try again.'); 
     setSubmitted(false); 
     return; 
   }
    // const score = {
    //   total: totalQuestions,
    //   correct: correctAnswers,
    //   percentage: Math.round((correctAnswers / totalQuestions) * 100),
    //   details: results
    // };
   console.log(
     'Navigating to quiz-result page with state:', 
     `/student-dashboard/courses/ongoing/${topicId}/protocols/quiz-result/${courseId}`, 
     { 
       total: totalQuestions, 
       correct: correctAnswers, 
       percentage, 
       details: results, 
       topicId, 
       registration_id: registrationId, 
       courseTitle: currentCourse.title 
     } 
   );
    // Navigate to results page with score data
    setTimeout(() => {
      navigate(`/student-dashboard/courses/ongoing/${topicId}/protocols/quiz-result/${courseId}`, {
        state: { 
        total: totalQuestions, 
        correct: correctAnswers, 
        percentage, 
        details: results, 
        topicId,
        registration_id: registrationId,
        courseTitle: currentCourse.title
        }
      });
    }, 1000);
  };

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-8">
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4">
            <span className="text-xl text-white">📝</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{currentCourse.title}</h1>
            <p className="text-gray-600 mt-1">{currentCourse.description}</p>
          </div>
        </div>

        {/* Quiz Content */}
        {currentCourse.questions.length > 0 ? (
          <div className="space-y-6">
            {currentCourse.questions.map((q) => (
              <div key={q.id} className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">{q.question}</h2>
                  <div className="space-y-3">
                    {q.options.map((option, index) => {
                      const isMultipleAnswer = Array.isArray(q.answer);
                      const isSelected = isMultipleAnswer 
                        ? (answers[q.id] || []).includes(option)
                        : answers[q.id] === option;

                      return (
                        <label 
                          key={index}
                          className={`flex items-center p-3 rounded-lg border transition-all cursor-pointer
                            ${isSelected 
                              ? 'bg-blue-50 border-blue-500' 
                              : 'border-gray-200 hover:bg-gray-50'}`}
                        >
                          <input
                            type={isMultipleAnswer ? "checkbox" : "radio"}
                            name={`question-${q.id}`}
                            value={option}
                            checked={isSelected}
                            onChange={() => handleAnswerChange(q.id, option)}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="ml-3 text-gray-700">{option}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}

            {/* Submit Button */}
            <div className="flex justify-end mt-8">
              <button
                onClick={handleSubmit}
                disabled={submitted}
                className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all
                  ${submitted 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'}`}
              >
                {submitted ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    Complete The Course
                    <span className="text-xl">✓</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-6 text-center">
            <p className="text-gray-600 text-lg">No questions available for this course.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
